import React from 'react';
import { AlertTriangle, Calendar, Check, Clock, Download, File, FileInput, Folder, FolderOpen, History, LayoutGrid, Loader2, Maximize2, Minimize2, RotateCcw, Satellite, Search, Sparkles, Upload, Workflow, X, XCircle } from 'lucide-react';
import { C } from '../theme.js';
import { Alert, Button, Code, Eyebrow, IconButton, Pill, SectionTitle, Select, StatusChip, TextInput } from '../components/Primitives.jsx';
import { INPUT_FILES, OUTPUT_FOLDERS, PHASE2_LABEL, PHASE2_TOOLTIP, SOURCE_COLUMNS, findSheetNameCollision, findSourceNameOwner, isReservedSheetName, replaceConfirmMessage, reservedSheetNameWarning, resolveFilenamePattern, sourceNameTakenMessage, summarizeStep } from '../data.js';
import { colLetter, parseFile, formatFileSize, maxUploadSizeLabel, setOutputDirHandle } from '../fileIO.js';
import { runOutputUrl } from '../api.js';

/* eslint-disable */
// ─── Data preview table + run history drawer ─────────────────────────────

// Detect cell anomalies so we can highlight them in the preview.
function cellFlags(row, col, runOrigRow) {
  const v = row[col.name];
  const s = v == null ? '' : String(v);
  const flags = [];
  const trimmedEmpty = s.trim() === '' || s.trim() === 'N/A' || s.trim() === '—';
  if (trimmedEmpty && s !== '') flags.push('blanky');
  else if (trimmedEmpty) flags.push('blank');
  if (/^\s|\s$/.test(s) && s.length) flags.push('whitespace');
  if (col.type === 'string' && /[A-Z]/.test(s) && /[a-z]/.test(s) && s !== s) flags.push('mixed');
  return flags;
}

// ── Download full result (Feature A) ──────────────────────────────────
// Format picker + a Button, disabled whenever the file it would produce
// might not match what's on screen (no step selected, a /preview request
// is pending, the step has never been previewed, or its last preview had
// 0 output rows — 0 is a legitimate answer, e.g. a Source-anchored step
// whose Source is itself empty, not something worth a separate state).
function DownloadStepButton({ step, stepInfo, pending, format, onFormatChange, onDownload }) {
  const outputCount = stepInfo?.outputCount ?? stepInfo?.rows?.length ?? 0;
  const disabled = !step || pending || !stepInfo || outputCount === 0;
  const title = !step ? 'Select a step to download'
    : pending ? 'Waiting for the preview to finish…'
    : (!stepInfo || outputCount === 0) ? 'No rows to download'
    : stepInfo.error ? 'This step failed — downloads its pass-through (unchanged input) rows'
    : `Download all ${outputCount.toLocaleString()} row(s) this step produced`;
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flexShrink: 0 }} title={title}>
      <Select
        value={format}
        onChange={onFormatChange}
        options={[{ value: 'csv', label: 'CSV' }, { value: 'xlsx', label: 'XLSX' }]}
        style={{ width: 76, height: 30, fontSize: '.72rem', padding: '0 24px 0 8px' }}
      />
      <Button
        kind="ghost" size="sm" icon="⬇"
        disabled={disabled}
        onClick={() => onDownload(format)}
      >Download</Button>
    </div>
  );
}

export function DataPreview({
  beforeRows,
  afterRows,
  // Must be the LIVE schema (source columns ∪ keys this step added). Passing
  // only the upload schema hides new columns such as ``<col>_scaled``.
  columns = SOURCE_COLUMNS,
  step,
  savedInputRows,
  collapsed, onToggleCollapse,
  height = 280,
  resizing = false,
  flashTick = 0,
  isFullscreen, onToggleFullscreen,
  // What the "View" picker can switch to besides the pipeline: kept sheets
  // and step outputs saved under an Output Name —
  // [{key, kind: 'sheet'|'step', name, rows, columns, stepLabel?, totalRows?, error?}].
  // `activeSource` null means the pipeline's own view (the default);
  // onSelectSource(key | null) switches.
  sources = [],
  activeSource = null,
  onSelectSource,
  // "Download full result" (Feature A) — the selected step's runMap entry
  // ({rows, outputCount, error, ...}, or null/undefined before it's ever
  // been previewed), whether a /preview request is currently pending
  // (debounce wait or the fetch itself), and the download callback, called
  // as onDownload(format). Always downloads the After output — no Before
  // option, see root changelog's Feature A entry.
  stepInfo = null,
  previewPending = false,
  onDownload,
  // Column → blocks highlight. `columnFids` maps column name → column-family id
  // for each view ({before, after, sheet}); clicking a header calls
  // onPickColumn(fid, name), and `focusFid` marks the picked column.
  columnFids = null,
  focusFid = null,
  onPickColumn,
  // 'step' = Before/After of the selected step; 'final' = always the
  // pipeline's final output (no Before view, no diff).
  scope = 'step',
  onScopeChange,
  // { before: Set, after: Set } of column names the selected step writes;
  // those columns get a light tint so the step's effect is easy to spot.
  modifiedColumns = null,
}) {
  const [tabState, setTab] = React.useState('after');
  const [showDiffState, setShowDiff] = React.useState(true);
  const isFinal = scope === 'final';
  // Final output has no Before side; the user's step-mode choices are kept
  // and come back when they switch to Current Step again.
  const tab = isFinal ? 'after' : tabState;
  const showDiff = isFinal ? false : showDiffState;
  const tintedCols = (!isFinal && modifiedColumns?.[tab === 'before' ? 'before' : 'after']) || null;
  const isTinted = (name) => !!tintedCols?.has(name);

  // When a step is selected, scroll the table sideways so the first column it
  // writes is in view. Runs once per selection: the tint only arrives with the
  // /preview lineage, so wait for it, then leave the scroll to the user.
  const tableScrollRef = React.useRef(null);
  const scrolledForStepRef = React.useRef(null);
  const firstTinted = tintedCols ? columns.find(c => tintedCols.has(c.name))?.name : undefined;
  React.useEffect(() => {
    const stepId = step?.id ?? null;
    if (isFinal || activeSource) return;
    if (scrolledForStepRef.current === stepId) return;
    if (!firstTinted) return;
    const box = tableScrollRef.current;
    const th = box?.querySelector(`th[data-col="${CSS.escape(firstTinted)}"]`);
    if (!box || !th) return;
    scrolledForStepRef.current = stepId;
    const left = th.offsetLeft;
    const right = left + th.offsetWidth;
    if (left >= box.scrollLeft && right <= box.scrollLeft + box.clientWidth) return;
    box.scrollTo({ left: Math.max(0, left - 48), behavior: 'smooth' });
  }, [step?.id, firstTinted, isFinal, activeSource]);
  const [flashing, setFlashing] = React.useState(false);
  const [downloadFormat, setDownloadFormat] = React.useState('csv');

  // Briefly outline the preview when a test completes, so the user's eye lands here.
  React.useEffect(() => {
    if (!flashTick) return;
    setFlashing(true);
    const id = setTimeout(() => setFlashing(false), 1100);
    return () => clearTimeout(id);
  }, [flashTick]);

  // Hard cap on rows this panel will ever build a diff for or render, no
  // matter what the caller passes. The server already caps a /preview
  // response to this same count (PREVIEW_ROW_CAP, routers/runs.py) — but a
  // caller can legitimately fall back to the full, uncapped upload (e.g.
  // the Source step's `afterRows`, before the first /preview response for
  // a freshly-opened pipeline has landed) and mapping tens or hundreds of
  // thousands of rows straight into <tr> elements is what hangs the
  // browser on a big file, not anything server-side. This is the one place
  // that guards against that regardless of why the array got large.
  const DISPLAY_ROW_CAP = 200;
  const totalIn = beforeRows.length;
  const totalOut = afterRows.length;
  const delta = totalOut - totalIn;
  const beforeTruncated = totalIn > DISPLAY_ROW_CAP;
  const afterTruncated = totalOut > DISPLAY_ROW_CAP;
  const beforeRowsShown = beforeTruncated ? beforeRows.slice(0, DISPLAY_ROW_CAP) : beforeRows;
  const afterRowsShown = afterTruncated ? afterRows.slice(0, DISPLAY_ROW_CAP) : afterRows;

  // Build a diff: map row id → "added/removed/changed". Computed off the
  // capped arrays above — when a side was truncated, "removed" and the
  // added/removed marks are necessarily approximate (a row outside the
  // first 200 on either side can't be compared), which is an acceptable
  // trade-off against freezing the tab; the Input/Output/Δ stats above
  // still use the true, untruncated totals.
  // Rows are only comparable when their id identifies exactly one row on each
  // side. Steps that reshape rows (Group By, Pivot, Unpivot, Union, …) emit
  // rows with no id or with one id repeated; keying a Map on that collapses
  // every row onto a single entry, so each row got compared against the wrong
  // "before" row and every cell looked changed. Such rows are left unmatched.
  const idCounts = (rows) => {
    const m = new Map();
    rows.forEach(r => { if (r.id != null) m.set(r.id, (m.get(r.id) || 0) + 1); });
    return m;
  };
  const beforeCounts = idCounts(beforeRowsShown);
  const afterCounts = idCounts(afterRowsShown);
  const matchable = (r) => r.id != null && beforeCounts.get(r.id) === 1 && afterCounts.get(r.id) === 1;
  const beforeMap = new Map(beforeRowsShown.filter(matchable).map(r => [r.id, r]));
  // Added/removed marks are trustworthy when every row on each side has an id
  // used once on that side (and neither side is truncated). A row only needs
  // to exist on BOTH sides to have its cells compared — requiring that here
  // too meant a row a Filter removed blocked every added/removed mark.
  const uniqueIds = (rows, counts) => rows.every(r => r.id != null && counts.get(r.id) === 1);
  const idsReliable = !beforeTruncated && !afterTruncated
    && uniqueIds(beforeRowsShown, beforeCounts) && uniqueIds(afterRowsShown, afterCounts);
  const beforeIds = new Set(beforeRowsShown.map(r => r.id));
  const afterIds = new Set(afterRowsShown.map(r => r.id));
  const removed = idsReliable ? beforeRowsShown.filter(r => !afterIds.has(r.id)) : [];
  // A new column isn't a changed value: only compare columns the before row had.
  const valueChanged = (beforeRow, row, name) =>
    !!beforeRow && name in beforeRow && String(beforeRow[name] ?? '') !== String(row[name] ?? '');

  const rowsForTab = tab === 'before' ? beforeRowsShown : afterRowsShown;
  const shownRows = rowsForTab;
  const tabTruncated = tab === 'before' ? beforeTruncated : afterTruncated;
  const tabTotal = tab === 'before' ? totalIn : totalOut;

  return (
    <div style={{
      borderTop: `1px solid ${C.borderStrong}`,
      background: C.bar,
      display: 'flex', flexDirection: 'column',
      ...(isFullscreen
        ? { flex: 1, minHeight: 0, width: '100%' }
        : { height: collapsed ? 42 : height }),
      transition: (isFullscreen || resizing) ? 'none' : 'height .25s ease',
      flexShrink: 0,
      minWidth: 0,
      animation: flashing ? 'dp-flash 1s ease-out' : 'none',
      position: 'relative',
    }}>
      {/* Indeterminate "refreshing" bar — visible the whole time a step's
          /preview is debouncing or in flight (previewPending), so an edit
          that hasn't finished re-running yet looks like it's working
          instead of looking like nothing happened. Shown even while
          collapsed, since the header strip is all that's visible then. */}
      {previewPending && (
        <div style={{
          position: 'absolute', top: 0, left: 0, right: 0, height: 2,
          overflow: 'hidden', zIndex: 5, pointerEvents: 'none',
        }}>
          <div style={{
            position: 'absolute', top: 0, height: '100%',
            background: C.accentBright,
            animation: 'omLoadBar 1.1s ease-in-out infinite',
          }} />
        </div>
      )}

      {/* Tab strip */}
      <div style={{
        height: 42, flexShrink: 0,
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '0 14px',
        borderBottom: collapsed ? 'none' : `1px solid ${C.border}`,
        flexWrap: 'nowrap', whiteSpace: 'nowrap', overflow: 'hidden',
      }}>
        <button
          onClick={onToggleCollapse}
          style={{
            background: 'transparent', border: 'none',
            color: C.fg4, fontFamily: 'inherit', fontSize: '.82rem',
            cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6,
            padding: '4px 6px', flexShrink: 0,
          }}
        >
          <span style={{ display: 'inline-block', transform: collapsed ? 'rotate(-90deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▾</span>
          <span style={{ color: C.fg2, fontWeight: 600 }}>Data preview</span>
        </button>
        <div style={{ width: 1, height: 18, background: C.border, margin: '0 8px', flexShrink: 0 }} />
        {sources.length > 0 && onSelectSource && (
          <>
            <Eyebrow style={{ flexShrink: 0 }}>View</Eyebrow>
            <div title="Show the pipeline, a source sheet, or a step's saved output" style={{ flexShrink: 0, marginRight: 8 }}>
              <Select
                value={activeSource?.key ?? PIPELINE_VIEW}
                onChange={(v) => onSelectSource(v === PIPELINE_VIEW ? null : v)}
                optgroups={sourceViewGroups(sources)}
                style={{ padding: '4px 28px 4px 8px', fontSize: '.74rem', maxWidth: 220 }}
              />
            </div>
          </>
        )}
        <Eyebrow style={{ flexShrink: 0 }}>
          {activeSource ? (activeSource.kind === 'sheet' ? 'Sheet' : 'Saved by') : isFinal ? 'Final output' : 'Step'}
        </Eyebrow>
        <span style={{
          color: C.fg2, fontSize: '.78rem', fontWeight: 600,
          overflow: 'hidden', textOverflow: 'ellipsis',
          minWidth: 0, flexShrink: 1,
        }}>
          {activeSource
            ? (activeSource.kind === 'sheet' ? activeSource.name : activeSource.stepLabel)
            : step ? (step.label || summarizeStep(step)) : '—'}
        </span>
        <div style={{ flex: 1, minWidth: 4 }} />
        {previewPending && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 5, flexShrink: 0,
            color: C.fg4, fontSize: '.72rem', fontWeight: 600, marginRight: 8,
          }}>
            <Loader2 size={12} style={{ animation: 'omSpin .8s linear infinite' }} />
            Refreshing…
          </span>
        )}
        {!collapsed && (
          <>
            {activeSource ? (
              <Pill tone="ghost" style={{ flexShrink: 0 }}>
                {(activeSource.totalRows ?? activeSource.rows.length).toLocaleString()} rows
              </Pill>
            ) : savedInputRows != null ? (
              <Pill tone="ghost" style={{ flexShrink: 0 }}>{savedInputRows.toLocaleString()} rows on file — re-upload to preview</Pill>
            ) : isFinal ? (
              <Pill tone="ghost" style={{ flexShrink: 0 }}>{totalOut} rows</Pill>
            ) : (
              <>
                <Pill tone="ghost" style={{ flexShrink: 0 }}>{totalIn} IN</Pill>
                <span style={{ color: C.fg5, margin: '0 4px', flexShrink: 0 }}>→</span>
                <Pill tone={delta < 0 ? 'warn' : delta > 0 ? 'ok' : 'ghost'} style={{ flexShrink: 0 }}>
                  {totalOut} OUT {delta !== 0 ? `(${delta > 0 ? '+' : ''}${delta})` : ''}
                </Pill>
              </>
            )}
            {onScopeChange && !activeSource && (
              <>
                <div style={{ width: 8, flexShrink: 0 }} />
                <div title="Show data at the step selected on the canvas, or the pipeline's final result" style={{
                  display: 'flex', background: C.input,
                  border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: 2,
                  flexShrink: 0,
                }}>
                  {[['step', 'Current Step'], ['final', 'Final Output']].map(([k, l]) => {
                    const on = scope === k;
                    return (
                      <button key={k} onClick={() => onScopeChange(k)} style={{
                        background: on ? 'var(--info-bg)' : 'transparent',
                        color: on ? C.accentBright : C.fg4,
                        border: 'none', borderRadius: 6, padding: '4px 10px',
                        fontFamily: 'inherit', fontWeight: 600, fontSize: '.72rem',
                        cursor: 'pointer', transition: 'all .15s',
                      }}>{l}</button>
                    );
                  })}
                </div>
              </>
            )}
            {!isFinal && !activeSource && (
            <>
            <div style={{ width: 8, flexShrink: 0 }} />
            <div style={{
              display: 'flex', background: C.input,
              border: `1px solid ${C.borderStrong}`, borderRadius: 8, padding: 2,
              flexShrink: 0,
            }}>
              {[['before', 'Before'], ['after', 'After']].map(([k, l]) => {
                const on = tab === k;
                return (
                  <button key={k} onClick={() => setTab(k)} style={{
                    background: on ? 'var(--info-bg)' : 'transparent',
                    color: on ? C.accentBright : C.fg4,
                    border: 'none', borderRadius: 6, padding: '4px 10px',
                    fontFamily: 'inherit', fontWeight: 600, fontSize: '.72rem',
                    cursor: 'pointer', transition: 'all .15s',
                  }}>{l}</button>
                );
              })}
            </div>
            <div style={{ width: 8, flexShrink: 0 }} />
            <label style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer',
              color: C.fg4, fontSize: '.72rem', flexShrink: 0,
            }} title="Highlight rows added/removed/changed">
              <input type="checkbox" checked={showDiff} onChange={(e) => setShowDiff(e.target.checked)}
                style={{ accentColor: C.accent }} />
              Diff
            </label>
            </>
            )}
            {onDownload && !activeSource && (
              <>
                <div style={{ width: 8, flexShrink: 0 }} />
                <DownloadStepButton
                  step={step}
                  stepInfo={stepInfo}
                  pending={previewPending}
                  format={downloadFormat}
                  onFormatChange={setDownloadFormat}
                  onDownload={onDownload}
                />
              </>
            )}
            {onToggleFullscreen && (
              <>
                <div style={{ width: 1, height: 18, background: C.border, margin: '0 4px 0 8px', flexShrink: 0 }} />
                <IconButton
                  title={isFullscreen ? 'Exit fullscreen (Esc)' : 'Fullscreen preview'}
                  onClick={onToggleFullscreen}
                >{isFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}</IconButton>
              </>
            )}
          </>
        )}
      </div>

      {/* The per-sheet tab bar was removed earlier because many tabs slowed
          down pipeline building; the "View" dropdown in the header replaces
          it — one control however many sheets and saved outputs exist. */}
      {!collapsed && activeSource ? (
        <SheetRawTable
          sheet={activeSource}
          fids={columnFids?.sheet}
          focusFid={focusFid}
          onPickColumn={onPickColumn}
        />
      ) : !collapsed && (
        <div ref={tableScrollRef} style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
          {tabTruncated && (
            <div style={{
              padding: '6px 14px', fontSize: '.72rem', color: C.fg5,
              background: 'var(--info-bg)', borderBottom: `1px solid ${C.border}`,
            }}>
              Showing the first {DISPLAY_ROW_CAP.toLocaleString()} of {tabTotal.toLocaleString()} rows — the full result is available via Download.
            </div>
          )}
          <table style={{
            borderCollapse: 'separate', borderSpacing: 0,
            width: '100%', fontSize: '.78rem',
            fontFamily: 'Inter, sans-serif',
          }}>
            <thead style={stickyHead}>
              <ColumnLetterRow columns={columns} />
              <tr>
                <th style={th}>#</th>
                {columns.map(col => (
                  <th key={col.name} data-col={col.name} {...columnHeaderProps(columnFids?.[tab === 'before' ? 'before' : 'after']?.[col.name], col.name, focusFid, onPickColumn,
                    isTinted(col.name) ? { ...th, background: MODIFIED_COL_HEAD_BG } : th)}>
                    <ColumnHeaderLabel col={col}
                      picked={!!onPickColumn && !!focusFid && columnFids?.[tab === 'before' ? 'before' : 'after']?.[col.name] === focusFid} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {shownRows.map((row, idx) => {
                const wasRemoved = idsReliable && tab === 'before' && !afterIds.has(row.id);
                const wasAdded = idsReliable && tab === 'after' && !beforeIds.has(row.id);
                const beforeRow = matchable(row) ? beforeMap.get(row.id) : undefined;
                const changed = tab === 'after' && columns.some(c => valueChanged(beforeRow, row, c.name));

                const rowBg = !showDiff ? (idx % 2 ? 'var(--bg-input)' : 'transparent')
                  : wasRemoved ? 'rgba(248,113,113,.08)'
                    : wasAdded ? 'rgba(52,211,153,.08)'
                      : changed ? 'rgba(96,165,250,.06)'
                        : (idx % 2 ? 'var(--bg-input)' : 'transparent');

                // row.id is undefined for every step past the first —
                // server-computed rows never carry it, only the client's
                // initial upload tagging does — so fall back to idx or
                // every row in this table shares key=undefined.
                return (
                  <tr key={row.id ?? idx} style={{ background: rowBg }}>
                    <td style={{ ...td, color: C.fg5, fontFamily: "'JetBrains Mono', monospace", fontSize: '.72rem' }}>
                      {wasRemoved && <span title="Removed by this step" style={{ color: C.error, marginRight: 4 }}>−</span>}
                      {wasAdded && <span title="Added by this step" style={{ color: C.ok, marginRight: 4 }}>+</span>}
                      {idx + 1}
                    </td>
                    {columns.map(col => {
                      const v = row[col.name];
                      const flags = cellFlags(row, col, beforeRow);
                      const oldV = beforeRow ? beforeRow[col.name] : undefined;
                      const cellChanged = showDiff && tab === 'after' && valueChanged(beforeRow, row, col.name);
                      return (
                        <td key={col.name} style={{
                          ...td,
                          color: cellChanged ? C.accentBright : C.fg3,
                          fontFamily: col.type === 'number' || col.type === 'date'
                            ? "'JetBrains Mono', monospace"
                            : 'Inter, sans-serif',
                          background: cellChanged ? 'rgba(59,130,246,.10)'
                            : isTinted(col.name) ? MODIFIED_COL_BG : 'transparent',
                        }}>
                          {flags.includes('blank') ? (
                            <span style={{ color: C.fg6, fontStyle: 'italic' }}>∅</span>
                          ) : flags.includes('whitespace') ? (
                            <span title="leading/trailing whitespace" style={{ color: C.warn }}>
                              <span style={{ color: 'var(--warn-border)' }}>·</span>
                              {String(v).trim()}
                              <span style={{ color: 'var(--warn-border)' }}>·</span>
                            </span>
                          ) : v === '' || v == null ? (
                            <span style={{ color: C.fg6, fontStyle: 'italic' }}>∅</span>
                          ) : (
                            String(v)
                          )}
                          {cellChanged && (
                            <span title={`was: ${oldV}`} style={{ marginLeft: 5, color: C.fg5, fontSize: '.7rem', textDecoration: 'line-through' }}>
                              {oldV === '' || oldV == null ? '∅' : String(oldV)}
                            </span>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
              {/* Removed-rows footer for "after" view */}
              {tab === 'after' && showDiff && removed.length > 0 && (
                <>
                  <tr>
                    <td colSpan={columns.length + 1} style={{
                      padding: '8px 14px',
                      background: 'rgba(248,113,113,.08)',
                      borderTop: `1px solid ${C.border}`,
                      color: C.error, fontSize: '.74rem',
                      textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
                    }}>
                      ↓ {removed.length} row(s) dropped by this step
                    </td>
                  </tr>
                  {removed.slice(0, 6).map((row, idx) => (
                    <tr key={'rm-' + (row.id ?? idx)} style={{ background: 'rgba(248,113,113,.04)', opacity: 0.7 }}>
                      <td style={{ ...td, color: C.error }}>−</td>
                      {columns.map(col => (
                        <td key={col.name} style={{
                          ...td,
                          color: C.fg4, textDecoration: 'line-through',
                          fontFamily: col.type === 'number' || col.type === 'date'
                            ? "'JetBrains Mono', monospace" : 'Inter, sans-serif',
                        }}>
                          {row[col.name] === '' || row[col.name] == null ? '∅' : String(row[col.name])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// Header-cell props that make a column header clickable: a click asks the
// editor to highlight every block that reads or writes this column. `fid` is
// the column's family id (see engine/column_usage.py); without one (no
// preview data yet, or a column this view has no lineage for) the header
// stays plain. The picked column gets a solid accent background with white
// text (ColumnHeaderLabel), legible in both themes — the old soft-accent
// background was dark navy in the light theme, under dark header text.
function columnHeaderProps(fid, name, focusFid, onPick, baseStyle) {
  if (!fid || !onPick) return { style: baseStyle };
  const picked = focusFid === fid;
  return {
    style: {
      ...baseStyle, cursor: 'pointer',
      ...(picked ? { background: C.accentStrong, boxShadow: `inset 0 -2px 0 ${C.accentDeep}` } : null),
    },
    title: picked
      ? 'Click to clear the highlight'
      : 'Click to highlight the blocks that read or write this column',
    onClick: () => onPick(fid, name),
  };
}

// The two-line header content (name, then type). White on the picked
// column's solid accent background, theme colours otherwise.
function ColumnHeaderLabel({ col, picked }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span style={{ color: picked ? '#fff' : C.fg2, fontWeight: 700 }}>{col.name}</span>
      <span style={{ color: picked ? 'rgba(255,255,255,.8)' : C.fg5, fontSize: '.62rem', textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 600 }}>{col.type}</span>
    </div>
  );
}

// Tint for columns the selected step writes (Current Step mode). Amber so it
// reads differently from the blue changed-cell diff highlight. The header
// tint is opaque because the header row is sticky.
const MODIFIED_COL_BG = 'rgba(251,191,36,.08)';
const MODIFIED_COL_HEAD_BG = `color-mix(in srgb, ${C.bar} 86%, rgb(251,191,36))`;

// Excel-style column letters (A, B, C…) above the header row, assigned by
// column position — the same position → letter mapping column_key() uses on
// the server — so a user typing a letter into a per-row column reference
// can see which column it points at.
function ColumnLetterRow({ columns }) {
  return (
    <tr>
      <th style={letterTh} />
      {columns.map((col, i) => (
        <th key={col.name} style={letterTh}>{colLetter(i)}</th>
      ))}
    </tr>
  );
}

// Raw view of a source sheet or a step's saved output — no before/after/diff
// concept applies, so this is a plain table rather than sharing the
// diff-aware rendering above.
function SheetRawTable({ sheet, fids, focusFid, onPickColumn }) {
  if (!sheet) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.fg5, fontSize: '.82rem' }}>
        Sheet not found.
      </div>
    );
  }
  const rows = sheet.rows || [];
  const columns = sheet.columns || [];
  const note = sheet.error
    ? `This step failed: ${sheet.error}`
    : sheet.kind === 'step' && !rows.length
      ? 'No rows yet — the preview hasn\'t produced this output.'
      : sheet.totalRows > rows.length
        ? `Showing the first ${rows.length.toLocaleString()} of ${sheet.totalRows.toLocaleString()} rows — select the step and use Download for the full result.`
        : null;
  return (
    <div style={{ flex: 1, overflow: 'auto', minHeight: 0 }}>
      {note && (
        <div style={{
          padding: '6px 14px', fontSize: '.72rem', color: sheet.error ? C.error : C.fg5,
          background: 'var(--info-bg)', borderBottom: `1px solid ${C.border}`,
        }}>{note}</div>
      )}
      <table style={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%', fontSize: '.78rem', fontFamily: 'Inter, sans-serif' }}>
        <thead style={stickyHead}>
          <ColumnLetterRow columns={columns} />
          <tr>
            <th style={th}>#</th>
            {columns.map(col => (
              <th key={col.name} {...columnHeaderProps(fids?.[col.name], col.name, focusFid, onPickColumn, th)}>
                <ColumnHeaderLabel col={col} picked={!!onPickColumn && !!focusFid && fids?.[col.name] === focusFid} />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, idx) => (
            <tr key={row.id ?? idx} style={{ background: idx % 2 ? 'var(--bg-input)' : 'transparent' }}>
              <td style={{ ...td, color: C.fg5, fontFamily: "'JetBrains Mono', monospace", fontSize: '.72rem' }}>{idx + 1}</td>
              {columns.map(col => {
                const v = row[col.name];
                return (
                  <td key={col.name} style={{
                    ...td, color: C.fg3,
                    fontFamily: col.type === 'number' || col.type === 'date' ? "'JetBrains Mono', monospace" : 'Inter, sans-serif',
                  }}>
                    {v === '' || v == null ? <span style={{ color: C.fg6, fontStyle: 'italic' }}>∅</span> : String(v)}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// The View picker's options: the pipeline first, then sheets, then saved
// step outputs (empty groups left out).
const PIPELINE_VIEW = '__pipeline__';
function sourceViewGroups(sources) {
  const sheets = sources.filter(s => s.kind === 'sheet').map(s => ({ value: s.key, label: s.name }));
  const outputs = sources.filter(s => s.kind === 'step').map(s => ({ value: s.key, label: `${s.name} (${s.stepLabel})` }));
  return [
    { label: 'Pipeline', options: [{ value: PIPELINE_VIEW, label: 'Pipeline' }] },
    ...(sheets.length ? [{ label: 'Source sheets', options: sheets }] : []),
    ...(outputs.length ? [{ label: 'Saved step outputs', options: outputs }] : []),
  ];
}

const th = {
  textAlign: 'left',
  padding: '8px 12px',
  background: C.bar,
  borderBottom: `1px solid ${C.borderStrong}`,
  color: C.fg2,
  whiteSpace: 'nowrap',
};
// The whole <thead> sticks (not each <th>), so the letter row and the
// header row stay pinned together without stacking offsets.
const stickyHead = { position: 'sticky', top: 0, zIndex: 1 };
const letterTh = {
  ...th,
  padding: '3px 12px',
  borderBottom: `1px solid ${C.border}`,
  color: C.fg5,
  fontFamily: "'JetBrains Mono', monospace",
  fontSize: '.64rem', fontWeight: 600,
};
const td = {
  padding: '6px 12px',
  borderBottom: `1px solid ${C.borderSubtle}`,
  whiteSpace: 'nowrap',
  verticalAlign: 'top',
};

// ── Run history drawer ────────────────────────────────────────────────
export function RunHistoryDrawer({ open, onClose, runs }) {
  const [selected, setSelected] = React.useState(runs[0]?.id);
  const cur = runs.find(r => r.id === selected) || runs[0];
  // The drawer stays mounted while closed, so its selection outlived each
  // new run. Opening it, or a new run arriving while it's open, selects the
  // newest run.
  const newestId = runs[0]?.id;
  React.useEffect(() => {
    if (open) setSelected(newestId);
  }, [open, newestId]);

  if (!open) return null;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(15, 17, 23, 0.55)',
      backdropFilter: 'blur(2px)',
      zIndex: 90,
      display: 'flex', flexDirection: 'column', justifyContent: 'flex-end',
    }} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={{
        height: '64vh',
        background: C.app,
        borderTop: `1px solid ${C.borderStrong}`,
        boxShadow: '0 -8px 24px rgba(0,0,0,.4)',
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '14px 24px', borderBottom: `1px solid ${C.border}`,
        }}>
          <span style={{ display: 'inline-flex', color: C.fg3 }}><History size={18} /></span>
          <span style={{ color: C.fg1, fontWeight: 700, fontSize: '1rem' }}>Run history</span>
          <Pill tone="opt">{runs.length} runs</Pill>
          <div style={{ flex: 1 }} />
          <Button kind="ghost" size="sm" onClick={onClose}>Close</Button>
        </div>

        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Run list */}
          <div style={{
            width: 340, flexShrink: 0,
            borderRight: `1px solid ${C.border}`,
            overflowY: 'auto',
          }}>
            {runs.map(r => {
              const on = r.id === cur?.id;
              return (
                <div key={r.id}
                  onClick={() => setSelected(r.id)}
                  style={{
                    padding: '12px 16px',
                    borderBottom: `1px solid ${C.border}`,
                    cursor: 'pointer',
                    background: on ? 'var(--info-bg)' : 'transparent',
                    borderLeft: `3px solid ${on ? C.accent : 'transparent'}`,
                    transition: 'all .12s',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center' }}>
                    <span style={{
                      color: C.fg1, fontWeight: 600, fontSize: '.82rem',
                      fontFamily: "'JetBrains Mono', monospace",
                    }}>{r.id}</span>
                    <StatusChip status={r.status} />
                  </div>
                  <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 4, display: 'flex', alignItems: 'center', gap: 5 }}>
                    <Calendar size={11} /> {r.startedAt}
                  </div>
                  <div style={{ color: C.fg4, fontSize: '.74rem', marginTop: 2 }}>
                    {r.rowsIn} → {r.rowsOut} rows
                    {r.rowsDropped > 0 && <span style={{ color: C.warn, marginLeft: 6 }}>(−{r.rowsDropped})</span>}
                    {r.durationMs > 0 && <span style={{ color: C.fg5, marginLeft: 8 }}>· {r.durationMs} ms</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Run detail */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
            {cur ? <RunDetail run={cur} /> : <div style={{ color: C.fg5 }}>No runs yet.</div>}
          </div>
        </div>
      </div>
    </div>
  );
}

function RunDetail({ run }) {
  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
        <span style={{
          color: C.fg1, fontWeight: 700, fontSize: '1.2rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{run.id}</span>
        <StatusChip status={run.status} />
      </div>
      <div style={{ color: C.fg5, fontSize: '.82rem', marginBottom: 18, display: 'flex', alignItems: 'center', gap: 12 }}>
        <span>Started {run.startedAt} · by {run.user}</span>
        {run.status === 'done' && (
          <a
            href={runOutputUrl(run.id)}
            download={(run.outputPath || '').split('/').pop() || ''}
            style={{ textDecoration: 'none' }}
          >
            <Button kind="ghost" size="sm" icon={<Download size={14} />}>Download output</Button>
          </a>
        )}
      </div>

      {run.status === 'error' && (
        <Alert kind="error" style={{ marginBottom: 16 }}>
          <b>Pipeline halted:</b> {run.error}
        </Alert>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 20 }}>
        {[
          { label: 'Rows in', value: run.rowsIn, tone: 'neutral' },
          { label: 'Rows out', value: run.rowsOut, tone: 'ok' },
          { label: 'Rows dropped', value: run.rowsDropped, tone: 'warn' },
          { label: 'Duration', value: run.durationMs > 0 ? `${run.durationMs} ms` : '—', tone: 'neutral' },
        ].map(s => (
          <div key={s.label} style={{
            background: C.surface, border: `1px solid ${C.border}`,
            borderRadius: 8, padding: '10px 14px',
          }}>
            <Eyebrow>{s.label}</Eyebrow>
            <div style={{
              fontFamily: "'JetBrains Mono', monospace", fontSize: '1.25rem',
              fontWeight: 600, marginTop: 4,
              color: s.tone === 'warn' ? C.warn : s.tone === 'ok' ? C.ok : C.fg1,
            }}>{s.value}</div>
          </div>
        ))}
      </div>

      <SectionTitle>Output file</SectionTitle>
      <div style={{
        background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
        padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <span style={{
          background: 'var(--ok-bg)', border: `1px solid #065f34`, borderRadius: 4,
          color: C.ok, fontSize: '.66rem', fontWeight: 700, letterSpacing: '.04em',
          padding: '3px 6px',
        }}>CSV</span>
        <span style={{
          fontFamily: "'JetBrains Mono', monospace", color: C.accentBright,
          fontSize: '.78rem', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>{run.outputPath}</span>
        {run.status === 'done' && <Button kind="success" size="sm">⬇ Download</Button>}
      </div>

      <SectionTitle>Step-by-step log</SectionTitle>
      <RunLog status={run.status} run={run} />
    </>
  );
}

function RunLog({ status, run }) {
  // Synthetic but realistic per-step log
  const okSteps = [
    { t: '14:08:12.001', step: 'source', msg: 'Reading customers_export_2026-05.csv (142 KB)', rows: 24 },
    { t: '14:08:12.062', step: 'transform', msg: "Trim whitespace on column 'email' → 3 cells changed", rows: 24 },
    { t: '14:08:12.071', step: 'transform', msg: "Lowercase 'email' → 2 cells changed", rows: 24 },
    { t: '14:08:12.094', step: 'dedupe', msg: 'Dedupe on [email] → dropped 3 duplicate rows', rows: 21 },
    { t: '14:08:12.142', step: 'filter', msg: 'Drop test accounts (OR · 3 conditions) → dropped 3 rows', rows: 18 },
    { t: '14:08:12.178', step: 'transform', msg: "Title-case 'name' → 4 cells changed", rows: 18 },
    { t: '14:08:12.198', step: 'transform', msg: "Upper 'country' → 2 cells changed", rows: 18 },
    { t: '14:08:12.412', step: 'output', msg: 'Wrote ' + run.outputPath, rows: 18 },
  ];
  const errSteps = [
    { t: '16:04:20.002', step: 'source', msg: 'Reading customers_export_2026-05.csv (142 KB)' },
    { t: '16:04:20.014', step: 'source', msg: 'IOError: file locked by another process (lockfile=customers_export_2026-05.csv.lck)', kind: 'error' },
  ];
  const lines = status === 'error' ? errSteps : okSteps;
  return (
    <div style={{
      background: C.input, border: `1px solid ${C.borderStrong}`, borderRadius: 8,
      padding: '12px 14px',
      fontFamily: "'JetBrains Mono', monospace",
      fontSize: '.76rem', lineHeight: 1.65,
    }}>
      {lines.map((l, i) => (
        <div key={i} style={{
          display: 'flex', gap: 12, padding: '2px 0',
          color: l.kind === 'error' ? C.error : C.fg3,
        }}>
          <span style={{ color: C.fg5, flexShrink: 0 }}>{l.t}</span>
          <span style={{
            color: C.accentBright, flexShrink: 0, minWidth: 84,
            textTransform: 'uppercase', letterSpacing: '.04em',
          }}>{l.step}</span>
          <span style={{ flex: 1 }}>{l.msg}</span>
          {l.rows != null && <span style={{ color: C.fg5, flexShrink: 0 }}>{l.rows} rows</span>}
        </div>
      ))}
    </div>
  );
}

// ── File browser modal (Replace input source file) ────────────────────
// Mock file picker — lists CSV / XLS / XLSX files across a few synthetic
// locations. Search + format filter + grouped list. Calls onPick(file)
// with the chosen INPUT_FILES entry on confirm.
const FILE_FORMATS = [
  { id: 'all', label: 'All supported', exts: 'csv, tsv, xlsx, xlsm, xls, xlsb' },
  { id: 'csv', label: 'CSV', exts: '.csv' },
  { id: 'xlsx', label: 'Excel · XLSX', exts: '.xlsx' },
  { id: 'xls', label: 'Excel · XLS', exts: '.xls' },
];
const FB_LOCATIONS = [
  { id: 'recent', label: 'Recent', icon: <Clock size={14} /> },
  { id: 'project', label: 'This project', icon: <Folder size={14} /> },
  { id: 'samples', label: 'Sample data', icon: <Sparkles size={14} /> },
  { id: 'all', label: 'All locations', icon: <LayoutGrid size={14} /> },
];

// Glob → RegExp for filename patterns. `*` matches any run of chars, `?` one
// char. Empty pattern matches everything (used by the folder-watch picker).
function globToRegex(pattern) {
  if (!pattern) return /.*/;
  const escaped = pattern
    .replace(/[.+^${}()|[\]\\]/g, '\\$&')
    .replace(/\*/g, '.*')
    .replace(/\?/g, '.');
  return new RegExp('^' + escaped + '$', 'i');
}

const PATTERN_EXAMPLES = [
  { pat: '*.csv', note: 'any CSV' },
  { pat: 'sales_*.xlsx', note: 'XLSX starting with “sales_”' },
  { pat: 'invoices_*.xls', note: 'legacy XLS exports' },
  { pat: '*_2026-*.csv', note: 'dated 2026 CSV files' },
];

export function FileBrowserModal({ open, onClose, onPick: commitPick, checkPick, currentFile, currentPipelineId, projects = [], existingSheetNames = [], sourceEntries = [] }) {
  // Every pick goes through `checkPick` (supplied by the caller) before it's
  // committed: `blocked` stops it with an error, `replaces` lists existing
  // same-kind names it would overwrite and asks the user to confirm first.
  const [pendingReplace, setPendingReplace] = React.useState(null);
  const [pickError, setPickError] = React.useState(null);
  const onPick = (payload) => {
    const verdict = checkPick?.(payload) || {};
    if (verdict.blocked) { setPickError(verdict.blocked); return; }
    if (verdict.replaces?.length) { setPendingReplace({ names: verdict.replaces, payload }); return; }
    commitPick(payload);
  };
  const initialMode = currentFile?.chained ? 'chain'
    : currentFile?.dynamic ? 'watch'
      : 'browse';
  const [mode, setMode] = React.useState(initialMode);
  const [location, setLocation] = React.useState('recent');
  const [format, setFormat] = React.useState('all');
  const [query, setQuery] = React.useState('');
  const [selectedName, setSelectedName] = React.useState(currentFile?.name || null);
  const [watchPath, setWatchPath] = React.useState(currentFile?.watchPath || '/data/inbox/');
  const [watchPattern, setWatchPattern] = React.useState(currentFile?.watchPattern || '');
  const [chainPipelineId, setChainPipelineId] = React.useState(currentFile?.sourcePipelineId || null);
  const [chainQuery, setChainQuery] = React.useState('');
  const [uploadResult, setUploadResult] = React.useState(null);
  const [uploadError, setUploadError] = React.useState(null);
  const [uploading, setUploading] = React.useState(false);
  // Multi-sheet workbook: which of result.sheetNames to keep. `null` means
  // "use all sheets" (the default); a Set means a custom subset.
  const [keptSheets, setKeptSheets] = React.useState(null);
  // User-assigned record-set name per raw sheet name — {rawName: assignedName}.
  // Defaults to the raw name until edited; see UploadBody's name field.
  const [sheetNames, setSheetNames] = React.useState({});
  // Which sheet drives this pipeline's steps — a build-target OPTION KEY
  // (see buildTargetOptions below), not a bare name, since a real sheet and
  // a blank sheet could otherwise share the same typed name. Only
  // meaningful when there's more than one candidate — see UploadBody's
  // "Build target" section. Required + explicit: null until the user
  // actively confirms, even though the UI suggests the first one.
  const [buildTarget, setBuildTarget] = React.useState(null);
  const [buildTargetConfirmed, setBuildTargetConfirmed] = React.useState(false);
  // Blank sheets added in this same upload action — [{tempId, name}]. Most
  // of the time these are exactly what the pipeline is meant to build INTO
  // (fill with formulas/logic computed from the real sheets), so — unlike
  // Stage 1's assumption — a blank sheet IS a valid build-target candidate,
  // not just an inert extra named source. Created here, at upload time, per
  // the "no in-session switching" decision — the old mid-session "+" in
  // Data Preview's tab bar was removed for this reason.
  const [blankSheets, setBlankSheets] = React.useState([]);
  const blankSheetIdRef = React.useRef(0);

  // Kept sheets from the REAL uploaded file, in workbook order.
  const keptRealSheetNames = uploadResult?.sheetNames
    ? uploadResult.sheetNames.filter(n => !keptSheets || keptSheets.has(n))
    : [];
  // Every build-target candidate — real sheets first (in workbook order),
  // then blank sheets in creation order — tagged with a stable key so two
  // candidates that happen to share a typed name (a real sheet and a blank
  // one, say) don't collide as radio values the way bare names would.
  const buildTargetOptions = [
    ...keptRealSheetNames.map(rawName => ({
      optionKey: `real:${rawName}`, kind: 'real', rawName,
      label: sheetNames[rawName] ?? rawName,
    })),
    ...blankSheets.map(s => ({
      optionKey: `blank:${s.tempId}`, kind: 'blank', tempId: s.tempId,
      label: s.name.trim() || '(untitled)',
    })),
  ];
  // A change to keptSheets/blankSheets can invalidate the current build
  // target (its sheet may have been excluded or removed) — treat that the
  // same as "not yet confirmed."
  const buildTargetStillValid = buildTarget && buildTargetOptions.some(o => o.optionKey === buildTarget);
  const suggestedBuildTarget = buildTargetOptions[0]?.optionKey ?? null;
  const effectiveBuildTarget = buildTargetStillValid ? buildTarget : suggestedBuildTarget;

  const onChangeKeptSheetsAndResetTarget = (next) => {
    setKeptSheets(next);
    setBuildTargetConfirmed(false);
  };
  const onChangeBuildTarget = (optionKey) => {
    setBuildTarget(optionKey);
    setBuildTargetConfirmed(false);
  };
  const onAddBlankSheet = () => {
    blankSheetIdRef.current += 1;
    setBlankSheets(prev => [...prev, { tempId: blankSheetIdRef.current, name: '' }]);
    setBuildTargetConfirmed(false);
  };
  const onChangeBlankSheetName = (tempId, name) => {
    setBlankSheets(prev => prev.map(s => (s.tempId === tempId ? { ...s, name } : s)));
    setBuildTargetConfirmed(false);
  };
  const onRemoveBlankSheet = (tempId) => {
    setBlankSheets(prev => prev.filter(s => s.tempId !== tempId));
    setBuildTargetConfirmed(false);
  };

  // Reset transient UI when the modal re-opens.
  React.useEffect(() => {
    if (open) {
      setMode(currentFile?.chained ? 'chain' : currentFile?.dynamic ? 'watch' : 'browse');
      setSelectedName(currentFile?.name || null);
      setQuery('');
      setLocation('recent');
      setFormat('all');
      setWatchPath(currentFile?.watchPath || '/data/inbox/');
      setWatchPattern(currentFile?.watchPattern || '');
      setChainPipelineId(currentFile?.sourcePipelineId || null);
      setChainQuery('');
      setUploadResult(null);
      setUploadError(null);
      setUploading(false);
      setKeptSheets(null);
      setSheetNames({});
      setBuildTarget(null);
      setBuildTargetConfirmed(false);
      setBlankSheets([]);
      setPendingReplace(null);
      setPickError(null);
    }
  }, [open, currentFile]);
  // A pending replace-confirm or pick error describes one exact pick; any
  // change to what would be picked invalidates it.
  React.useEffect(() => {
    setPendingReplace(null);
    setPickError(null);
  }, [mode, selectedName, uploadResult, keptSheets, sheetNames, blankSheets, buildTarget, buildTargetConfirmed, watchPath, watchPattern, chainPipelineId]);

  if (!open) return null;

  // Keyboard: Esc cancels, Enter picks if a row is selected.
  const supported = ['csv', 'xls', 'xlsx'];
  const all = INPUT_FILES.filter(f => supported.includes(f.format));
  const filtered = all
    .filter(f => location === 'all' || f.location === location)
    .filter(f => format === 'all' || f.format === format)
    .filter(f => !query.trim() ||
      f.name.toLowerCase().includes(query.toLowerCase()) ||
      f.folder.toLowerCase().includes(query.toLowerCase()));

  // Group by folder for nicer scanning.
  const groups = new Map();
  for (const f of filtered) {
    if (!groups.has(f.folder)) groups.set(f.folder, []);
    groups.get(f.folder).push(f);
  }

  const selected = filtered.find(f => f.name === selectedName);
  const isCurrent = selected && currentFile && !currentFile.dynamic && selected.name === currentFile.name;

  // Watch-mode matching: glob over the mock file pool, sorted by date asc.
  const watchRegex = globToRegex(watchPattern.trim());
  const watchMatches = INPUT_FILES
    .filter(f => supported.includes(f.format) && watchRegex.test(f.name))
    .slice()
    .sort((a, b) => (a.modified || '').localeCompare(b.modified || ''));
  const watchValid = watchPath.trim().length > 0;
  const watchUnchanged = currentFile?.dynamic
    && currentFile.watchPath === watchPath
    && (currentFile.watchPattern || '') === watchPattern.trim();

  // Chain-mode catalog: flatten projects → pipelines, exclude self.
  const chainCatalog = [];
  for (const proj of projects) {
    for (const pl of (proj.pipelines || [])) {
      if (pl.id === currentPipelineId) continue;
      chainCatalog.push({ project: proj, pipeline: pl });
    }
  }
  const chainFiltered = chainCatalog.filter(({ project, pipeline }) => {
    const q = chainQuery.trim().toLowerCase();
    if (!q) return true;
    return pipeline.name.toLowerCase().includes(q)
      || project.name.toLowerCase().includes(q)
      || (pipeline.description || '').toLowerCase().includes(q);
  });
  const chainSelected = chainCatalog.find(c => c.pipeline.id === chainPipelineId) || null;
  const chainUnchanged = currentFile?.chained
    && currentFile.sourcePipelineId === chainPipelineId;

  // At least one sheet must stay kept — a multi-sheet workbook with every
  // checkbox unchecked has nothing to upload.
  const uploadHasKeptSheet = !uploadResult?.sheetNames
    || uploadResult.sheetNames.some(n => !keptSheets || keptSheets.has(n));
  // Every kept real sheet AND every blank sheet needs a non-empty,
  // non-reserved name before confirming. Names that will actually be created
  // as sheets must also be unique within this upload and not already held by
  // an uploaded file or a step's Output Name — a hard block, matching
  // upsert_sheet's 409. Re-using an existing SHEET name is allowed: the pick
  // is confirmed as a replacement before it's committed.
  const createdSheetNames = [
    ...(uploadResult?.sheetNames?.length > 1 ? keptRealSheetNames.map(n => (sheetNames[n] ?? n).trim()) : []),
    ...blankSheets.map(s => s.name.trim()),
  ];
  const uploadHasValidNames = [
    ...keptRealSheetNames.map(n => (sheetNames[n] ?? n).trim()),
    ...blankSheets.map(s => s.name.trim()),
  ].every(n => n && !isReservedSheetName(n))
    && createdSheetNames.every((n, i) => !findSheetNameCollision(createdSheetNames.filter((_, j) => j !== i), n)
      && !findSourceNameOwner(sourceEntries, n, { kind: 'sheet' }));
  // Explicit + required build-target confirmation, only meaningful when
  // there's more than one candidate (real or blank) to choose among.
  const uploadBuildTargetOk = buildTargetOptions.length <= 1 || buildTargetConfirmed;

  const handleUploadFile = (file) => {
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);
    setKeptSheets(null);
    setSheetNames({});
    setBuildTarget(null);
    setBuildTargetConfirmed(false);
    setBlankSheets([]);
    parseFile(file)
      .then((result) => {
        result._rawFile = file;  // stash for server upload
        setUploadResult(result);
        setUploading(false);
      })
      .catch((err) => {
        setUploadError(err.message || 'Failed to parse file');
        setUploading(false);
      });
  };

  const confirm = () => {
    if (mode === 'upload') {
      if (!uploadResult) return;
      const ext = (uploadResult.fileName.split('.').pop() || 'csv').toLowerCase();
      // Multi-sheet workbook: `keptSheets` null means "use all sheets"
      // (the default). Which sheet drives the pipeline is now an explicit,
      // required choice (buildTarget/effectiveBuildTarget above) whenever
      // there's more than one candidate — no longer implicitly "whichever
      // is first in the workbook," and the candidate can be a BLANK sheet
      // too (the common case: fill a blank sheet with logic computed from
      // the real ones). Any sheet not chosen becomes an extra named source
      // (server/app/routers/datasets.py).
      const allSheetNames = uploadResult.sheetNames || [];
      const keptNames = allSheetNames.filter(n => !keptSheets || keptSheets.has(n));
      const assignedNameFor = (rawName) => (sheetNames[rawName] ?? rawName).trim() || rawName;
      // A CSV/TSV has no sheets at all (no `sheetNames`/`allSheets`), so
      // there's no raw sheet name to look up — its single table is the
      // primary input as-is.
      const isWorkbook = keptNames.length > 0;
      const chosen = buildTargetOptions.length > 1
        ? buildTargetOptions.find(o => o.optionKey === effectiveBuildTarget)
        : isWorkbook
          ? { kind: 'real', rawName: keptNames[0], label: assignedNameFor(keptNames[0]) }
          : { kind: 'real', rawName: undefined, label: undefined };
      // A blank sheet chosen as build target has no rows/columns of its own
      // (that's the point — the pipeline's steps are what will fill it) and
      // no corresponding sheet inside the uploaded workbook, so there's
      // nothing for the server to re-parse on reload; `blankBuildTarget`
      // tells it to skip that entirely instead of looking for a sheet name
      // that will never exist in the file (server/app/routers/pipelines.py).
      const primarySheet = chosen?.kind === 'real' && chosen.rawName && uploadResult.allSheets
        ? uploadResult.allSheets[chosen.rawName]
        : chosen?.kind === 'real' && !isWorkbook
          ? { rows: uploadResult.rows, columns: uploadResult.columns }
          : { rows: [], columns: [] };
      // EVERY kept REAL sheet — the build target included, if it's one of
      // them — gets its own named tab, so a workbook's first/chosen sheet
      // isn't the one sheet you can't see. Empty for a CSV or a
      // single-sheet workbook. Blank sheets (including one chosen as build
      // target) are appended the same way, with no rows/columns of their
      // own — they're still a Data Preview tab / named source either way.
      const sheets = [
        ...(allSheetNames.length > 1
          ? keptNames.map(name => ({ name: assignedNameFor(name), ...uploadResult.allSheets[name] }))
          : []),
        ...blankSheets
          .filter(s => s.name.trim())
          .map(s => ({ name: s.name.trim(), rows: [], columns: [] })),
      ];
      onPick({
        uploaded: true,
        _parsedRows: primarySheet.rows,
        _parsedColumns: primarySheet.columns,
        _rawFile: uploadResult._rawFile,
        // The RAW workbook sheet name the primary input's data came from —
        // distinct from that sheet's user-assigned display name above.
        // Sent to the server (api.uploadInputFile) so it can re-parse the
        // SAME sheet on every future reload instead of defaulting to the
        // workbook's first sheet (server/app/engine/input_parser.py).
        // Omitted (and blankBuildTarget sent instead) when the chosen
        // build target is a blank sheet, not a real one.
        sheetName: chosen?.kind === 'real' ? chosen.rawName : undefined,
        blankBuildTarget: chosen?.kind === 'blank' || undefined,
        // The build target's assigned display name — see api.js's
        // uploadInputFile for why this is sent as a distinct field from
        // sheetName (which is always the RAW workbook name for a real
        // target, never the possibly-renamed display name).
        buildTargetName: chosen?.label || undefined,
        sheets,
        name: uploadResult.fileName,
        format: ext === 'tsv' ? 'csv' : (['xlsx', 'xlsm', 'xls', 'xlsb'].includes(ext) ? ext : 'csv'),
        size: formatFileSize(uploadResult.fileSize),
        rows: primarySheet.rows.length,
      });
      return;
    }
    if (mode === 'watch') {
      if (!watchValid || watchUnchanged) return;
      const pattern = watchPattern.trim();
      const next = watchMatches[0]; // mock “next up” file the watcher would pick
      onPick({
        dynamic: true,
        watchPath: watchPath.trim(),
        watchPattern: pattern,
        name: pattern ? `${watchPath.trim()}${pattern}` : `${watchPath.trim()}*`,
        format: next?.format || 'csv',
        size: next ? next.size : ' — ',
        rows: next?.rows || 0,
        nextUp: next ? next.name : null,
        matchCount: watchMatches.length,
      });
      return;
    }
    if (mode === 'chain') {
      if (!chainSelected || chainUnchanged) return;
      const { project, pipeline } = chainSelected;
      const filenamePattern = pipeline.output?.filename || pipeline.outputName || 'output';
      const fmt = pipeline.output?.format || 'csv';
      const resolved = resolveFilenamePattern(
        filenamePattern.replace(/\.[^.]+$/, '') + '.' + fmt,
        { runId: 'R-026-A93', inputFile: pipeline.inputFile?.name || 'input.csv', seq: 1 }
      );
      onPick({
        chained: true,
        sourcePipelineId: pipeline.id,
        sourcePipelineName: pipeline.name,
        sourceProjectId: project.id,
        sourceProjectName: project.name,
        sourceOutputFolder: pipeline.output?.folder || '',
        sourceOutputPattern: filenamePattern,
        sourceLastRun: pipeline.lastRunAt ? new Date(pipeline.lastRunAt).toLocaleString() : '—',
        sourceStatus: pipeline.status,
        name: resolved,
        format: fmt,
        size: '—',
        rows: pipeline.rowsProcessed || 0,
      });
      return;
    }
    if (!selected || isCurrent) return;
    onPick(selected);
  };

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'Enter') confirm();
      }}
      tabIndex={-1}
      style={{
        position: 'fixed', inset: 0, zIndex: 2100, // above the floating inspector (2000)
        background: 'rgba(15, 17, 23, 0.6)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(900px, 100%)',
          height: 'min(620px, 100%)',
          background: C.app,
          border: `1px solid ${C.borderStrong}`,
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Title bar */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 12,
          background: C.bar,
        }}>
          <span style={{ display: 'inline-flex', color: C.fg3 }}>{mode === 'watch' ? <Satellite size={18} /> : mode === 'chain' ? <Workflow size={18} /> : <FileInput size={18} />}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.94rem' }}>
              Choose input file
            </div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 1 }}>
              {mode === 'watch'
                ? 'Pipeline runs on every new file the watcher finds. CSV, XLS, XLSX.'
                : mode === 'chain'
                  ? 'Use the output of another pipeline as this pipeline’s input.'
                  : 'Replaces the source file for this pipeline. Supports CSV, XLS, and XLSX.'}
            </div>
          </div>
          <IconButton title="Close (Esc)" onClick={onClose}><X size={14} /></IconButton>
        </div>

        {/* Mode tabs */}
        <div style={{
          padding: '10px 20px',
          borderBottom: `1px solid ${C.border}`,
          background: C.app,
          display: 'flex', alignItems: 'center', gap: 4,
        }}>
          {[
            { id: 'browse', label: 'Pick a file', icon: <File size={15} />, sub: 'Static input' },
            { id: 'upload', label: 'Upload file', icon: <Upload size={15} />, sub: 'Local file' },
            { id: 'watch', label: 'Watch a folder', icon: <Satellite size={15} />, sub: 'Dynamic input', phase2: true },
            { id: 'chain', label: 'Pick a pipeline', icon: <Workflow size={15} />, sub: 'Chained input', phase2: true },
          ].map(t => {
            const on = mode === t.id;
            return (
              <button
                key={t.id}
                onClick={t.phase2 ? undefined : () => setMode(t.id)}
                disabled={t.phase2}
                title={t.phase2 ? PHASE2_TOOLTIP : undefined}
                style={{
                  opacity: t.phase2 ? .45 : 1,
                  display: 'flex', alignItems: 'center', gap: 9,
                  padding: '7px 12px',
                  background: on ? C.input : 'transparent',
                  border: `1px solid ${on ? C.borderStrong : 'transparent'}`,
                  borderRadius: 8,
                  cursor: t.phase2 ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  textAlign: 'left',
                }}
              >
                <span style={{ display: 'inline-flex', color: on ? C.accentBright : C.fg4 }}>{t.icon}</span>
                <div style={{ lineHeight: 1.15 }}>
                  <div style={{
                    color: on ? C.fg1 : C.fg3,
                    fontSize: '.82rem', fontWeight: 600,
                  }}>{t.label}</div>
                  <div style={{
                    color: on ? C.accentBright : C.fg5,
                    fontSize: '.66rem', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '.08em',
                    marginTop: 1,
                  }}>{t.phase2 ? PHASE2_LABEL : t.sub}</div>
                </div>
              </button>
            );
          })}
        </div>

        {mode === 'browse' && (<>
          {/* Toolbar: search + format filter */}
          <div style={{
            padding: '10px 20px', borderBottom: `1px solid ${C.border}`,
            display: 'flex', alignItems: 'center', gap: 10,
          }}>
            <div style={{ position: 'relative', flex: 1, maxWidth: 340 }}>
              <span style={{
                position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                color: C.fg5, display: 'inline-flex', pointerEvents: 'none',
              }}><Search size={13} /></span>
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search files…"
                autoFocus
                style={{
                  width: '100%',
                  background: C.input,
                  border: `1px solid ${C.borderStrong}`,
                  borderRadius: 8,
                  padding: '7px 11px 7px 30px',
                  color: C.fg1,
                  fontSize: '.82rem',
                  fontFamily: 'inherit',
                  outline: 'none',
                }}
              />
            </div>

            <div style={{ flex: 1 }} />

            <span style={{
              color: C.fg5, fontSize: '.72rem',
              textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
            }}>Filter</span>
            <Select
              value={format}
              onChange={setFormat}
              options={FILE_FORMATS.map(f => ({
                value: f.id,
                label: `${f.label}  ·  ${f.exts}`,
              }))}
              style={{ width: 220, padding: '6px 11px', fontSize: '.78rem' }}
            />
          </div>

          {/* Body: left rail locations + main list */}
          <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
            {/* Locations rail */}
            <div style={{
              width: 180, flexShrink: 0,
              borderRight: `1px solid ${C.border}`,
              background: C.bar,
              padding: '10px 6px',
              display: 'flex', flexDirection: 'column', gap: 2,
            }}>
              {FB_LOCATIONS.map(loc => {
                const on = location === loc.id;
                const count = on
                  ? filtered.length
                  : all.filter(f => loc.id === 'all' || f.location === loc.id)
                    .filter(f => format === 'all' || f.format === format).length;
                return (
                  <button
                    key={loc.id}
                    onClick={() => setLocation(loc.id)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '7px 10px',
                      background: on ? 'var(--info-bg)' : 'transparent',
                      color: on ? C.accentBright : C.fg3,
                      border: 'none',
                      borderLeft: `3px solid ${on ? C.accent : 'transparent'}`,
                      borderRadius: 0,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: '.82rem',
                      fontWeight: on ? 600 : 500,
                      textAlign: 'left',
                    }}
                  >
                    <span style={{ display: 'inline-flex' }}>{loc.icon}</span>
                    <span style={{ flex: 1 }}>{loc.label}</span>
                    <span style={{
                      color: on ? C.accentBright : C.fg5,
                      fontFamily: "'JetBrains Mono', monospace",
                      fontSize: '.7rem',
                    }}>{count}</span>
                  </button>
                );
              })}
            </div>

            {/* File list */}
            <div style={{ flex: 1, overflowY: 'auto', minWidth: 0 }}>
              {/* Column header */}
              <div style={fbHeaderRow}>
                <div style={{ ...fbCol, flex: '1 1 auto', color: C.fg4 }}>Name</div>
                <div style={{ ...fbCol, width: 72, color: C.fg4 }}>Format</div>
                <div style={{ ...fbCol, width: 90, color: C.fg4, textAlign: 'right' }}>Size</div>
                <div style={{ ...fbCol, width: 92, color: C.fg4, textAlign: 'right' }}>Rows</div>
                <div style={{ ...fbCol, width: 132, color: C.fg4 }}>Modified</div>
              </div>

              {filtered.length === 0 && (
                <div style={{
                  padding: '60px 24px', textAlign: 'center', color: C.fg5,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'center', opacity: .5, marginBottom: 8 }}><Search size={28} /></div>
                  <div style={{ color: C.fg3, fontWeight: 600, marginBottom: 4 }}>
                    No matching files
                  </div>
                  <div style={{ fontSize: '.78rem', lineHeight: 1.5 }}>
                    Try a different location or clear the search.
                  </div>
                </div>
              )}

              {[...groups.entries()].map(([folder, files]) => (
                <div key={folder}>
                  <div style={{
                    padding: '8px 16px 4px',
                    color: C.fg5, fontSize: '.68rem',
                    textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
                    background: C.bar,
                    borderTop: `1px solid ${C.borderSubtle}`,
                    borderBottom: `1px solid ${C.borderSubtle}`,
                    display: 'flex', alignItems: 'center', gap: 6,
                  }}>
                    <span style={{ color: C.fg6, display: 'inline-flex' }}><Folder size={12} /></span>
                    <span style={{
                      fontFamily: "'JetBrains Mono', monospace",
                      color: C.fg4,
                    }}>{folder}</span>
                    <span style={{
                      color: C.fg6, fontFamily: "'JetBrains Mono', monospace",
                      fontWeight: 500,
                    }}>· {files.length}</span>
                  </div>
                  {files.map(f => (
                    <FileRow
                      key={f.name}
                      file={f}
                      isSelected={selectedName === f.name}
                      isCurrent={currentFile && currentFile.name === f.name}
                      onSelect={() => setSelectedName(f.name)}
                      onDoubleClick={() => {
                        setSelectedName(f.name);
                        if (!(currentFile && currentFile.name === f.name)) onPick(f);
                      }}
                    />
                  ))}
                </div>
              ))}
            </div>
          </div>
        </>)}

        {mode === 'watch' && (
          <WatchFolderBody
            path={watchPath}
            pattern={watchPattern}
            onPathChange={setWatchPath}
            onPatternChange={setWatchPattern}
            matches={watchMatches}
          />
        )}

        {mode === 'chain' && (
          <ChainPipelineBody
            catalog={chainFiltered}
            allCount={chainCatalog.length}
            query={chainQuery}
            onQueryChange={setChainQuery}
            selectedId={chainPipelineId}
            onSelect={setChainPipelineId}
          />
        )}

        {mode === 'upload' && (
          <UploadBody
            onFileSelected={handleUploadFile}
            uploading={uploading}
            error={uploadError}
            result={uploadResult}
            keptSheets={keptSheets}
            onChangeKeptSheets={onChangeKeptSheetsAndResetTarget}
            sheetNames={sheetNames}
            onChangeSheetName={(raw, assigned) => setSheetNames(prev => ({ ...prev, [raw]: assigned }))}
            existingSheetNames={existingSheetNames}
            sourceEntries={sourceEntries}
            buildTargetOptions={buildTargetOptions}
            buildTarget={effectiveBuildTarget}
            onChangeBuildTarget={onChangeBuildTarget}
            buildTargetConfirmed={buildTargetConfirmed}
            onToggleBuildTargetConfirmed={setBuildTargetConfirmed}
            blankSheets={blankSheets}
            onAddBlankSheet={onAddBlankSheet}
            onChangeBlankSheetName={onChangeBlankSheetName}
            onRemoveBlankSheet={onRemoveBlankSheet}
          />
        )}

        {(pendingReplace || pickError) && (
          <div style={{ padding: '10px 20px', borderTop: `1px solid ${C.border}`, background: C.bar }}>
            {pickError ? (
              <Alert kind="error">{pickError}</Alert>
            ) : (
              <Alert kind="warn">
                <div>{replaceConfirmMessage(pendingReplace.names)}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
                  <Button
                    kind="primary" size="sm"
                    onClick={() => { const { payload } = pendingReplace; setPendingReplace(null); commitPick(payload); }}
                  >Replace</Button>
                  <Button kind="ghost" size="sm" onClick={() => setPendingReplace(null)}>Rename</Button>
                </div>
              </Alert>
            )}
          </div>
        )}

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          background: C.bar,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          {mode === 'upload' ? (
            uploadResult ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1,
              }}>
                <FormatBadge format={(() => { const e = (uploadResult.fileName.split('.').pop() || 'csv').toLowerCase(); return ['xlsx', 'xlsm', 'xls', 'xlsb'].includes(e) ? e : 'csv'; })()} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    color: C.fg1, fontSize: '.82rem', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{uploadResult.fileName}</div>
                  <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 1 }}>
                    {formatFileSize(uploadResult.fileSize)} · {uploadResult.rows.length.toLocaleString()} rows · {uploadResult.columns.length} columns
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1, color: C.fg5, fontSize: '.78rem',
              }}>{uploading ? 'Parsing file…' : 'Drop or pick a CSV, XLSX, or XLS file to upload.'}</div>
            )
          ) : mode === 'browse' ? (
            selected ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1,
              }}>
                <FormatBadge format={selected.format} />
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    color: C.fg1, fontSize: '.82rem', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    fontFamily: "'JetBrains Mono', monospace",
                  }}>{selected.name}</div>
                  <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 1 }}>
                    {selected.size.trim()} · {selected.rows.toLocaleString()} rows · {selected.folder}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1, color: C.fg5, fontSize: '.78rem',
              }}>Pick a file to replace the current input.</div>
            )
          ) : mode === 'watch' ? (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1,
            }}>
              <span style={{
                width: 38, height: 30, borderRadius: 4,
                background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                color: C.accentBright, fontSize: '1rem',
                flexShrink: 0,
              }}><Satellite size={16} /></span>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{
                  color: C.fg1, fontSize: '.82rem', fontWeight: 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  fontFamily: "'JetBrains Mono', monospace",
                }}>
                  {watchPath.trim() || '—'}
                  <span style={{ color: C.accentBright }}>
                    {watchPattern.trim() || '*'}
                  </span>
                </div>
                <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 1 }}>
                  {watchValid
                    ? `${watchMatches.length} file${watchMatches.length === 1 ? '' : 's'} match · runs on every new arrival`
                    : 'Enter a folder path to watch.'}
                </div>
              </div>
            </div>
          ) : (
            chainSelected ? (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1,
              }}>
                <span style={{
                  width: 38, height: 30, borderRadius: 4,
                  background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  color: C.accentBright, fontSize: '1rem',
                  flexShrink: 0,
                }}><Workflow size={16} /></span>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div style={{
                    color: C.fg1, fontSize: '.82rem', fontWeight: 600,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>{chainSelected.pipeline.name}</div>
                  <div style={{
                    color: C.fg5, fontSize: '.72rem', marginTop: 1,
                    fontFamily: "'JetBrains Mono', monospace",
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  }}>
                    {chainSelected.project.name} · output → {chainSelected.pipeline.output?.filename || '—'}.{chainSelected.pipeline.output?.format || 'csv'}
                  </div>
                </div>
              </div>
            ) : (
              <div style={{
                flex: 1, color: C.fg5, fontSize: '.78rem',
              }}>Pick a pipeline whose output becomes this pipeline's input.</div>
            )
          )}
          <Button kind="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button
            kind="primary" size="sm"
            disabled={
              mode === 'upload' ? (!uploadResult || uploading || !uploadHasKeptSheet || !uploadHasValidNames || !uploadBuildTargetOk)
                : mode === 'browse' ? (!selected || isCurrent)
                  : mode === 'watch' ? (!watchValid || watchUnchanged)
                    : (!chainSelected || chainUnchanged)
            }
            onClick={confirm}
          >
            {mode === 'upload'
              ? (uploading ? 'Parsing…' : !uploadResult ? 'Upload a file' : 'Use this file')
              : mode === 'browse'
                ? (isCurrent ? 'Already selected' : 'Use this file')
                : mode === 'watch'
                  ? (watchUnchanged ? 'Watcher unchanged' : 'Start watching')
                  : (chainUnchanged ? 'Already chained' : 'Chain this pipeline')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function WatchFolderBody({ path, pattern, onPathChange, onPatternChange, matches }) {
  const trimmed = (pattern || '').trim();
  const placeholder = '*.csv';
  const showAll = !trimmed;
  const previewLimit = 6;
  const previewed = matches.slice(0, previewLimit);

  return (
    <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
      {/* Left: config form */}
      <div style={{
        width: '46%', flexShrink: 0,
        borderRight: `1px solid ${C.border}`,
        padding: '18px 22px',
        overflowY: 'auto',
      }}>
        <div style={{
          color: C.fg5, fontSize: '.66rem',
          textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700,
          marginBottom: 8,
        }}>Watcher</div>

        <label style={{
          display: 'block',
          color: C.fg2, fontSize: '.78rem', fontWeight: 600,
          marginBottom: 5,
        }}>Folder to monitor</label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: C.fg5, display: 'inline-flex', pointerEvents: 'none',
          }}><Folder size={13} /></span>
          <input
            value={path}
            onChange={(e) => onPathChange(e.target.value)}
            placeholder="/data/inbox/"
            spellCheck={false}
            style={{
              width: '100%',
              background: C.input,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 8,
              padding: '8px 11px 8px 30px',
              color: C.fg1,
              fontSize: '.82rem',
              fontFamily: "'JetBrains Mono', monospace",
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 5, lineHeight: 1.5 }}>
          Local path, UNC share, or mounted bucket. New files arriving here will
          be processed automatically.
        </div>

        <div style={{ height: 16 }} />

        <label style={{
          display: 'flex', alignItems: 'baseline', justifyContent: 'space-between',
          color: C.fg2, fontSize: '.78rem', fontWeight: 600,
          marginBottom: 5,
        }}>
          <span>Filename pattern</span>
          <span style={{
            color: C.fg6, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
          }}>Optional</span>
        </label>
        <div style={{ position: 'relative' }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: C.fg5, fontSize: '.78rem', pointerEvents: 'none',
            fontFamily: "'JetBrains Mono', monospace",
          }}>⁎</span>
          <input
            value={pattern}
            onChange={(e) => onPatternChange(e.target.value)}
            placeholder={placeholder}
            spellCheck={false}
            style={{
              width: '100%',
              background: C.input,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 8,
              padding: '8px 11px 8px 30px',
              color: C.fg1,
              fontSize: '.82rem',
              fontFamily: "'JetBrains Mono', monospace",
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ color: C.fg5, fontSize: '.72rem', marginTop: 5, lineHeight: 1.5 }}>
          Wildcards: <Code>*</Code> matches any run of characters,
          {' '}<Code>?</Code> a single character.
          {' '}<span style={{ color: C.fg4 }}>Leave blank to pick up any file in the folder, in arrival order.</span>
        </div>

        <div style={{
          marginTop: 12,
          display: 'flex', flexWrap: 'wrap', gap: 6,
        }}>
          {PATTERN_EXAMPLES.map(ex => {
            const on = trimmed === ex.pat;
            return (
              <button
                key={ex.pat}
                title={ex.note}
                onClick={() => onPatternChange(ex.pat)}
                style={{
                  background: on ? 'var(--info-bg)' : C.input,
                  border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
                  color: on ? C.accentBright : C.fg3,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '.72rem',
                  padding: '4px 8px',
                  borderRadius: 6,
                  cursor: 'pointer',
                }}
              >{ex.pat}</button>
            );
          })}
        </div>

        <div style={{
          marginTop: 20, padding: '10px 12px',
          background: C.input,
          border: `1px dashed ${C.borderStrong}`,
          borderRadius: 8,
          color: C.fg4, fontSize: '.74rem', lineHeight: 1.55,
        }}>
          <div style={{
            color: C.accentBright, fontWeight: 700,
            fontSize: '.66rem', letterSpacing: '.08em',
            textTransform: 'uppercase', marginBottom: 4,
          }}>How it runs</div>
          When a new file lands in <Code>{path.trim() || '…'}</Code>
          {trimmed
            ? <> matching <Code>{trimmed}</Code></>
            : <> (any filename)</>}
          , the pipeline runs against it once.
          {!trimmed && ' Files are processed oldest first by modified time.'}
        </div>
      </div>

      {/* Right: matching preview */}
      <div style={{
        flex: 1, minWidth: 0,
        display: 'flex', flexDirection: 'column',
      }}>
        <div style={{
          padding: '12px 18px 10px',
          borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'baseline', gap: 8,
          background: C.bar,
        }}>
          <span style={{
            color: C.fg5, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700,
          }}>Matching now</span>
          <span style={{
            color: C.fg1, fontSize: '.82rem', fontWeight: 700,
            fontFamily: "'JetBrains Mono', monospace",
          }}>{matches.length}</span>
          <span style={{ color: C.fg5, fontSize: '.74rem' }}>
            {showAll ? 'files in folder' : 'files match'}
          </span>
          <div style={{ flex: 1 }} />
          <span style={{
            color: C.fg5, fontSize: '.68rem',
            fontFamily: "'JetBrains Mono', monospace",
          }}>sorted by modified ↑</span>
        </div>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          {previewed.length === 0 && (
            <div style={{
              padding: '60px 24px', textAlign: 'center', color: C.fg5,
            }}>
              <div style={{ display: 'flex', justifyContent: 'center', opacity: .5, marginBottom: 8 }}><Satellite size={26} /></div>
              <div style={{ color: C.fg3, fontWeight: 600, marginBottom: 4 }}>
                Nothing matches yet
              </div>
              <div style={{ fontSize: '.78rem', lineHeight: 1.5 }}>
                The watcher will pick up files matching this pattern
                as soon as they appear.
              </div>
            </div>
          )}

          {previewed.map((f, i) => (
            <div key={f.name} style={{
              display: 'flex', alignItems: 'center',
              padding: '9px 18px',
              borderBottom: `1px solid ${C.borderSubtle}`,
              background: i === 0 ? 'var(--info-bg)' : 'transparent',
              borderLeft: `3px solid ${i === 0 ? C.accent : 'transparent'}`,
            }}>
              <FileGlyph format={f.format} />
              <div style={{ marginLeft: 10, minWidth: 0, flex: 1 }}>
                <div style={{
                  color: i === 0 ? C.fg1 : C.fg2,
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: '.8rem', fontWeight: 600,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{f.name}</div>
                <div style={{
                  color: C.fg5, fontSize: '.7rem', marginTop: 1,
                  fontFamily: "'JetBrains Mono', monospace",
                }}>{f.modified} · {f.size.trim()} · {f.rows.toLocaleString()} rows</div>
              </div>
              {i === 0 && (
                <span style={{
                  background: C.input, border: `1px solid ${C.accentMuted}`,
                  color: C.accentBright,
                  fontSize: '.6rem', fontWeight: 700,
                  textTransform: 'uppercase', letterSpacing: '.08em',
                  padding: '2px 7px', borderRadius: 4,
                }}>Next up</span>
              )}
            </div>
          ))}

          {matches.length > previewLimit && (
            <div style={{
              padding: '10px 18px',
              color: C.fg5, fontSize: '.72rem',
              fontFamily: "'JetBrains Mono', monospace",
            }}>
              + {matches.length - previewLimit} more
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ChainPipelineBody({ catalog, allCount, query, onQueryChange, selectedId, onSelect }) {
  // Group catalog by project for two-column display
  const groups = new Map();
  for (const item of catalog) {
    if (!groups.has(item.project.id)) {
      groups.set(item.project.id, { project: item.project, pipelines: [] });
    }
    groups.get(item.project.id).pipelines.push(item.pipeline);
  }
  const statusTone = {
    active: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok, label: 'Active' },
    paused: { bg: 'var(--warn-bg)', bd: 'var(--warn-border)', fg: C.warn, label: 'Paused' },
    draft: { bg: 'var(--bg-raised)', bd: C.borderStrong, fg: C.fg4, label: 'Draft' },
  };
  const resolve = resolveFilenamePattern;

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      {/* Toolbar: search */}
      <div style={{
        padding: '10px 20px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', alignItems: 'center', gap: 10,
      }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 380 }}>
          <span style={{
            position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
            color: C.fg5, display: 'inline-flex', pointerEvents: 'none',
          }}><Search size={13} /></span>
          <input
            value={query}
            onChange={(e) => onQueryChange(e.target.value)}
            placeholder="Search pipelines…"
            autoFocus
            style={{
              width: '100%',
              background: C.input,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 8,
              padding: '7px 11px 7px 30px',
              color: C.fg1,
              fontSize: '.82rem',
              fontFamily: 'inherit',
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>
        <div style={{ flex: 1 }} />
        <span style={{
          color: C.fg5, fontSize: '.72rem',
          fontFamily: "'JetBrains Mono', monospace",
        }}>{catalog.length}/{allCount} pipelines</span>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        {catalog.length === 0 && (
          <div style={{
            padding: '60px 24px', textAlign: 'center', color: C.fg5,
          }}>
            <div style={{ display: 'flex', justifyContent: 'center', opacity: .5, marginBottom: 8 }}><Workflow size={26} /></div>
            <div style={{ color: C.fg3, fontWeight: 600, marginBottom: 4 }}>
              No matching pipelines
            </div>
            <div style={{ fontSize: '.78rem', lineHeight: 1.5 }}>
              Try clearing the search. The current pipeline is excluded —
              a pipeline can't chain to itself.
            </div>
          </div>
        )}
        {[...groups.values()].map(({ project, pipelines }) => (
          <div key={project.id}>
            <div style={{
              padding: '8px 20px 4px',
              color: C.fg5, fontSize: '.68rem',
              textTransform: 'uppercase', letterSpacing: '.08em', fontWeight: 700,
              background: C.bar,
              borderTop: `1px solid ${C.borderSubtle}`,
              borderBottom: `1px solid ${C.borderSubtle}`,
              display: 'flex', alignItems: 'center', gap: 8,
            }}>
              <span style={{ fontSize: '.9rem' }}>{project.icon || '📁'}</span>
              <span style={{ color: C.fg3, fontWeight: 700 }}>{project.name}</span>
              <span style={{
                color: C.fg6, fontFamily: "'JetBrains Mono', monospace", fontWeight: 500,
              }}>· {pipelines.length}</span>
            </div>
            {pipelines.map(pl => {
              const on = selectedId === pl.id;
              const tone = statusTone[pl.status] || statusTone.draft;
              const pattern = pl.output?.filename || pl.outputName || 'output';
              const fmt = pl.output?.format || 'csv';
              const sample = resolve(
                pattern.replace(/\.[^.]+$/, '') + '.' + fmt,
                { runId: 'R-026-A93', inputFile: pl.inputFile?.name || 'input.csv', seq: 1 }
              );
              return (
                <button
                  key={pl.id}
                  onClick={() => onSelect(pl.id)}
                  onDoubleClick={() => onSelect(pl.id)}
                  style={{
                    width: '100%', display: 'flex', alignItems: 'center', gap: 12,
                    padding: '11px 20px',
                    background: on ? 'var(--info-bg)' : 'transparent',
                    border: 'none',
                    borderLeft: `3px solid ${on ? C.accent : 'transparent'}`,
                    borderBottom: `1px solid ${C.borderSubtle}`,
                    cursor: 'pointer',
                    textAlign: 'left',
                    fontFamily: 'inherit',
                  }}
                >
                  <FormatBadge format={fmt} />
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{
                      display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2,
                    }}>
                      <span style={{
                        color: on ? C.fg1 : C.fg2,
                        fontSize: '.84rem', fontWeight: 600,
                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        minWidth: 0,
                      }}>{pl.name}</span>
                      <span style={{
                        flexShrink: 0,
                        background: tone.bg, border: `1px solid ${tone.bd}`, color: tone.fg,
                        fontSize: '.6rem', fontWeight: 700,
                        textTransform: 'uppercase', letterSpacing: '.06em',
                        padding: '1px 6px', borderRadius: 4,
                      }}>{tone.label}</span>
                    </div>
                    <div style={{
                      color: C.fg5, fontSize: '.72rem',
                      fontFamily: "'JetBrains Mono', monospace",
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                    }}>
                      <span style={{ color: C.accentBright }}>→</span> {sample}
                    </div>
                    <div style={{
                      color: C.fg6, fontSize: '.7rem', marginTop: 2,
                    }}>
                      {pl.schedule || 'Manual'}
                      {pl.lastRunAt && <> · last run {new Date(pl.lastRunAt).toLocaleString()}</>}
                      {pl.rowsProcessed ? <> · {pl.rowsProcessed.toLocaleString()} rows</> : null}
                    </div>
                  </div>
                  {on && (
                    <span style={{
                      flexShrink: 0,
                      background: C.input, border: `1px solid ${C.accentMuted}`,
                      color: C.accentBright,
                      fontSize: '.62rem', fontWeight: 700,
                      textTransform: 'uppercase', letterSpacing: '.08em',
                      padding: '2px 7px', borderRadius: 4,
                    }}>Chained</span>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Upload body (drag-and-drop + file picker) ──────────────────────────
function UploadBody({
  onFileSelected, uploading, error, result, keptSheets, onChangeKeptSheets,
  sheetNames = {}, onChangeSheetName, existingSheetNames = [],
  buildTargetOptions = [], buildTarget, onChangeBuildTarget,
  buildTargetConfirmed, onToggleBuildTargetConfirmed,
  blankSheets = [], onAddBlankSheet, onChangeBlankSheetName, onRemoveBlankSheet,
  sourceEntries = [],
}) {
  const inputRef = React.useRef(null);
  const [dragOver, setDragOver] = React.useState(false);

  const handleFile = (file) => { if (file) onFileSelected(file); };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  };

  if (uploading) {
    return (
      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40 }}>
        <div style={{ textAlign: 'center' }}>
          <span style={{
            display: 'inline-block', width: 28, height: 28,
            border: `3px solid ${C.accentMuted}`,
            borderTopColor: C.accentBright,
            borderRadius: '50%',
            animation: 'dp-spin .8s linear infinite',
          }} />
          <div style={{ color: C.fg2, fontSize: '.86rem', fontWeight: 600, marginTop: 14 }}>Parsing file…</div>
          <div style={{ color: C.fg5, fontSize: '.78rem', marginTop: 4 }}>Detecting columns and reading rows</div>
        </div>
      </div>
    );
  }

  if (result) {
    return (
      <div style={{ flex: 1, padding: '24px 28px', overflowY: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
          <span style={{
            width: 28, height: 28, borderRadius: '50%',
            background: 'var(--ok-bg)', border: '1px solid #065f34',
            color: C.ok,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            flexShrink: 0,
          }}><Check size={15} strokeWidth={2.5} /></span>
          <div>
            <div style={{ color: C.fg1, fontSize: '.9rem', fontWeight: 700 }}>File parsed successfully</div>
            <div style={{ color: C.fg5, fontSize: '.78rem', marginTop: 2 }}>
              {result.fileName} · {formatFileSize(result.fileSize)} · {result.rows.length.toLocaleString()} rows · {result.columns.length} columns
            </div>
          </div>
        </div>

        {(() => {
          // Every name assigned in this upload action — real kept sheets
          // (assigned name) + blank sheets — is validated the same way:
          // non-empty and non-reserved; unique within this batch; not already
          // held by an uploaded file or a step's Output Name. Those are hard
          // blocks (error border). Matching an EXISTING sheet is allowed but
          // gets the warning border — committing the upload asks the user to
          // confirm the replacement first. One consolidated message is shown
          // per problem type rather than repeating text under every row.
          const realRows = (result.sheetNames || []).map((rawName) => {
            const kept = !keptSheets || keptSheets.has(rawName);
            return { kind: 'real', key: rawName, rawName, kept, assigned: sheetNames[rawName] ?? rawName };
          });
          const blankRows = blankSheets.map(s => ({
            kind: 'blank', key: `blank-${s.tempId}`, tempId: s.tempId, kept: true, assigned: s.name,
          }));
          const allRows = [...realRows, ...blankRows];
          const showRealPanel = result.sheetNames && result.sheetNames.length > 1;
          // A single-sheet file's own sheet isn't saved as a named sheet.
          const isCreated = (r) => r.kept && (r.kind === 'blank' || showRealPanel);
          const rowsWithChecks = allRows.map((row) => {
            const assigned = row.assigned.trim();
            const created = isCreated(row) && !!assigned;
            const otherNames = allRows
              .filter(r => r.key !== row.key && isCreated(r))
              .map(r => r.assigned.trim());
            const reservedName = row.kept && !!assigned && isReservedSheetName(assigned);
            const duplicate = created && !reservedName && !!findSheetNameCollision(otherNames, assigned);
            const owner = created && !reservedName && !duplicate
              ? findSourceNameOwner(sourceEntries, assigned, { kind: 'sheet' })
              : null;
            const replaces = created && !reservedName && !duplicate && !owner
              ? findSheetNameCollision(existingSheetNames, assigned)
              : null;
            // `reserved` / `collision` drive the field borders below (error / warning).
            return {
              ...row, assigned, reservedName, duplicate, owner, replaces,
              reserved: reservedName || duplicate || !!owner, collision: replaces,
            };
          });
          const blockedRows = rowsWithChecks.filter(r => r.reserved);
          const replaceRows = rowsWithChecks.filter(r => r.collision);
          // Any sheet being named at all (real or blank) — the rule is shown
          // up front rather than only after a clash has been typed.
          const showNamingWarning = showRealPanel || blankSheets.length > 0;

          return (
            <>
              {showNamingWarning && (
                <div style={{ color: 'var(--warn-fg)', fontSize: '.72rem', marginBottom: 12, lineHeight: 1.4, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}><AlertTriangle size={13} /></span>
                  <span>Sheet names must be unique. Re-using the name of a sheet already in this pipeline
                  replaces it — you'll be asked to confirm first. A name already used by an uploaded
                  file or a step's Output Name, or used twice in this upload, can't be used. Matching is
                  case-insensitive (e.g. "Sales" and "sales" count as the same name).</span>
                </div>
              )}
              {showRealPanel && (
                <div style={{ marginBottom: 18, padding: 12, background: C.input, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{
                    color: C.fg5, fontSize: '.66rem', textTransform: 'uppercase',
                    letterSpacing: '.12em', fontWeight: 700, marginBottom: 8,
                  }}>Sheets in this file ({result.sheetNames.length})</div>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: C.fg2, cursor: 'pointer', marginBottom: 6 }}>
                    <input type="radio" name="sheet-mode" checked={!keptSheets} onChange={() => onChangeKeptSheets(null)} />
                    Use all sheets (default)
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: C.fg2, cursor: 'pointer' }}>
                    <input
                      type="radio" name="sheet-mode" checked={!!keptSheets}
                      onChange={() => onChangeKeptSheets(new Set(result.sheetNames))}
                    />
                    Custom selection
                  </label>
                  {/* Record-set name per kept sheet — always shown regardless of
                      "use all"/"custom selection", since every kept sheet needs a
                      name assigned before it's confirmed. Checkboxes only appear
                      in custom-selection mode; a sheet excluded there is greyed
                      out and skips validation (it won't be created). */}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10, paddingLeft: keptSheets ? 22 : 0 }}>
                    {rowsWithChecks.filter(r => r.kind === 'real').map(({ rawName, kept, assigned, reserved, collision }) => (
                      <div key={rawName} style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: kept ? 1 : 0.5 }}>
                        {keptSheets && (
                          <input
                            type="checkbox"
                            checked={keptSheets.has(rawName)}
                            onChange={(e) => {
                              const next = new Set(keptSheets);
                              if (e.target.checked) next.add(rawName); else next.delete(rawName);
                              onChangeKeptSheets(next);
                            }}
                          />
                        )}
                        <span style={{ fontSize: '.8rem', color: C.fg3, minWidth: 0, flexShrink: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {rawName} <span style={{ color: C.fg5 }}>({(result.allSheets?.[rawName]?.rows.length ?? 0).toLocaleString()} rows)</span>
                        </span>
                        <span style={{ color: C.fg5, fontSize: '.7rem', flexShrink: 0 }}>→ name:</span>
                        <TextInput
                          value={assigned}
                          onChange={(v) => onChangeSheetName?.(rawName, v)}
                          style={{
                            flex: 1, minWidth: 0,
                            padding: '4px 8px', fontSize: '.78rem',
                            borderColor: reserved ? 'var(--error-border)' : collision ? 'var(--warn-border)' : undefined,
                          }}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Blank sheets — created at upload time only, same naming/
                  collision/reserved-name rules as any real sheet. This is
                  usually exactly what the pipeline is meant to build INTO
                  (filled with formulas/logic computed from the real sheets
                  above), so a blank sheet IS a valid build-target choice
                  below, not just an inert extra named source. */}
              <div style={{ marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: blankSheets.length ? 8 : 0 }}>
                  <div style={{
                    color: C.fg5, fontSize: '.66rem', textTransform: 'uppercase',
                    letterSpacing: '.12em', fontWeight: 700,
                  }}>Blank sheets ({blankSheets.length})</div>
                  <Button kind="ghost" size="sm" onClick={onAddBlankSheet}>+ Add blank sheet</Button>
                </div>
                {blankSheets.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {rowsWithChecks.filter(r => r.kind === 'blank').map(({ tempId, assigned, reserved, collision }) => (
                      <div key={tempId} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <TextInput
                          value={assigned}
                          onChange={(v) => onChangeBlankSheetName(tempId, v)}
                          placeholder="e.g. Working Set"
                          style={{
                            flex: 1, minWidth: 0,
                            padding: '4px 8px', fontSize: '.78rem',
                            borderColor: reserved ? 'var(--error-border)' : collision ? 'var(--warn-border)' : undefined,
                          }}
                        />
                        <IconButton title="Remove this blank sheet" onClick={() => onRemoveBlankSheet(tempId)}><X size={14} /></IconButton>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {buildTargetOptions.length > 1 && (
                <div style={{ marginBottom: 18, padding: 12, background: C.input, border: `1px solid ${C.border}`, borderRadius: 8 }}>
                  <div style={{
                    color: C.fg5, fontSize: '.66rem', textTransform: 'uppercase',
                    letterSpacing: '.12em', fontWeight: 700, marginBottom: 6,
                  }}>Build target — required</div>
                  <div style={{ color: C.fg5, fontSize: '.74rem', marginBottom: 8, lineHeight: 1.5 }}>
                    Which sheet should this pipeline's steps run against? This can be a blank
                    sheet you're building — the others stay available as named sources but
                    won't drive the pipeline.
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {buildTargetOptions.map((opt) => (
                      <label key={opt.optionKey} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: '.82rem', color: C.fg2, cursor: 'pointer' }}>
                        <input
                          type="radio" name="build-target" checked={buildTarget === opt.optionKey}
                          onChange={() => onChangeBuildTarget(opt.optionKey)}
                        />
                        {opt.label}{opt.kind === 'blank' && <span style={{ color: C.fg5 }}> (blank)</span>}
                      </label>
                    ))}
                  </div>
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, fontSize: '.78rem', color: C.fg3, cursor: 'pointer', marginTop: 10 }}>
                    <input
                      type="checkbox" checked={buildTargetConfirmed}
                      onChange={(e) => onToggleBuildTargetConfirmed(e.target.checked)}
                      style={{ marginTop: 2 }}
                    />
                    <span>
                      Confirm — this pipeline will run its steps against{' '}
                      <strong>"{buildTargetOptions.find(o => o.optionKey === buildTarget)?.label}"</strong>.
                    </span>
                  </label>
                </div>
              )}

              {blockedRows.length > 0 && (
                <div style={{ color: 'var(--error-fg)', fontSize: '.72rem', marginBottom: 12, lineHeight: 1.4, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}><XCircle size={13} /></span>
                  <span>{blockedRows[0].reservedName
                    ? reservedSheetNameWarning(blockedRows[0].assigned)
                    : blockedRows[0].duplicate
                      ? `"${blockedRows[0].assigned}" is used more than once in this upload — sheet names must be unique.`
                      : sourceNameTakenMessage(blockedRows[0].assigned, blockedRows[0].owner)}
                  {blockedRows.length > 1 && ' (Other fields above are also affected.)'}</span>
                </div>
              )}
              {replaceRows.length > 0 && (
                <div style={{ color: 'var(--warn-fg)', fontSize: '.72rem', marginBottom: 12, lineHeight: 1.4, display: 'flex', gap: 6, alignItems: 'flex-start' }}>
                  <span style={{ display: 'inline-flex', flexShrink: 0, marginTop: 1 }}><AlertTriangle size={13} /></span>
                  <span>Naming this "{replaceRows[0].collision}" — an existing sheet — will replace it; you'll be
                  asked to confirm when you continue.
                  {replaceRows.length > 1 && ' (Other fields above are also affected — see the highlighted fields.)'}</span>
                </div>
              )}
            </>
          );
        })()}

        <div style={{
          color: C.fg5, fontSize: '.66rem',
          textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700,
          marginBottom: 8,
        }}>Detected schema</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {result.columns.map(c => (
            <span key={c.name} style={{
              background: C.input, border: `1px solid ${C.borderStrong}`,
              color: C.fg3, fontSize: '.74rem',
              padding: '4px 9px', borderRadius: 6,
              display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
              <span style={{ fontFamily: "'JetBrains Mono', monospace" }}>{c.name}</span>
              <span style={{
                fontSize: '.62rem', color: C.accentBright, textTransform: 'uppercase',
                letterSpacing: '.06em', fontWeight: 700,
              }}>{c.type}</span>
            </span>
          ))}
        </div>

        <div style={{
          color: C.fg5, fontSize: '.66rem',
          textTransform: 'uppercase', letterSpacing: '.12em', fontWeight: 700,
          marginTop: 16, marginBottom: 6,
        }}>Preview (first 5 rows)</div>
        <div style={{ overflow: 'auto', borderRadius: 8, border: `1px solid ${C.border}` }}>
          <table style={{ borderCollapse: 'collapse', width: '100%', fontSize: '.74rem', fontFamily: "'JetBrains Mono', monospace" }}>
            <thead>
              <tr>
                {result.columns.slice(0, 6).map(c => (
                  <th key={c.name} style={{ padding: '6px 10px', background: C.bar, borderBottom: `1px solid ${C.border}`, color: C.fg2, fontWeight: 700, textAlign: 'left', whiteSpace: 'nowrap' }}>{c.name}</th>
                ))}
                {result.columns.length > 6 && <th style={{ padding: '6px 10px', background: C.bar, borderBottom: `1px solid ${C.border}`, color: C.fg5 }}>…+{result.columns.length - 6}</th>}
              </tr>
            </thead>
            <tbody>
              {result.rows.slice(0, 5).map((row, i) => (
                <tr key={i}>
                  {result.columns.slice(0, 6).map(c => (
                    <td key={c.name} style={{ padding: '5px 10px', borderBottom: `1px solid ${C.borderSubtle}`, color: C.fg3, whiteSpace: 'nowrap', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {row[c.name] == null || row[c.name] === '' ? <span style={{ color: C.fg6, fontStyle: 'italic' }}>∅</span> : String(row[c.name])}
                    </td>
                  ))}
                  {result.columns.length > 6 && <td style={{ padding: '5px 10px', borderBottom: `1px solid ${C.borderSubtle}`, color: C.fg5 }}>…</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <button
          onClick={() => inputRef.current?.click()}
          style={{
            marginTop: 14, padding: '6px 12px',
            background: 'transparent', border: `1px solid ${C.borderStrong}`,
            borderRadius: 6, color: C.fg3, fontSize: '.78rem',
            fontFamily: 'inherit', cursor: 'pointer',
            display: 'inline-flex', alignItems: 'center', gap: 6,
          }}
        ><RotateCcw size={13} /> Choose different file</button>
        <input ref={inputRef} type="file" accept=".csv,.tsv,.xlsx,.xlsm,.xls,.xlsb" style={{ display: 'none' }}
          onChange={(e) => { if (e.target.files?.[0]) handleFile(e.target.files[0]); e.target.value = ''; }} />
      </div>
    );
  }

  return (
    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', padding: '24px 28px' }}>
      {error && (
        <Alert kind="error" style={{ marginBottom: 14 }}>
          {error}
        </Alert>
      )}
      <div
        onDrop={onDrop}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => inputRef.current?.click()}
        style={{
          flex: 1,
          border: `2px dashed ${dragOver ? C.accentBright : C.borderStrong}`,
          borderRadius: 12,
          background: dragOver ? 'rgba(59,130,246,.06)' : C.input,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer',
          transition: 'all .2s',
          minHeight: 200,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 12, opacity: 0.6 }}><File size={36} /></div>
        <div style={{ color: C.fg2, fontSize: '.9rem', fontWeight: 600, marginBottom: 4 }}>
          Drop a file here or click to browse
        </div>
        <div style={{ color: C.fg5, fontSize: '.78rem' }}>
          Supports CSV, TSV, XLSX, and XLS · Max size {maxUploadSizeLabel()}
        </div>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,.tsv,.xlsx,.xlsm,.xls,.xlsb"
        style={{ display: 'none' }}
        onChange={(e) => {
          if (e.target.files?.[0]) handleFile(e.target.files[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}

function FileRow({ file, isSelected, isCurrent, onSelect, onDoubleClick }) {
  const [hover, setHover] = React.useState(false);
  return (
    <div
      onClick={onSelect}
      onDoubleClick={onDoubleClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: 'flex', alignItems: 'center',
        padding: '8px 16px',
        background: isSelected ? 'var(--info-bg)' : hover ? C.surface : 'transparent',
        borderLeft: `3px solid ${isSelected ? C.accent : 'transparent'}`,
        borderBottom: `1px solid ${C.borderSubtle}`,
        cursor: 'pointer',
        transition: 'background-color .12s',
      }}
    >
      <div style={{ ...fbCol, flex: '1 1 auto', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <FileGlyph format={file.format} />
        <span style={{
          color: isSelected ? C.fg1 : C.fg2,
          fontSize: '.82rem', fontWeight: 600,
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          minWidth: 0,
        }}>{file.name}</span>
        {isCurrent && (
          <span style={{
            flexShrink: 0,
            background: C.input, border: `1px solid ${C.accentMuted}`,
            color: C.accentBright,
            fontSize: '.62rem', fontWeight: 700,
            textTransform: 'uppercase', letterSpacing: '.08em',
            padding: '1px 6px', borderRadius: 4,
          }}>Current</span>
        )}
      </div>
      <div style={{ ...fbCol, width: 72 }}>
        <FormatBadge format={file.format} small />
      </div>
      <div style={{
        ...fbCol, width: 90, color: C.fg4,
        fontFamily: "'JetBrains Mono', monospace", fontSize: '.74rem',
        textAlign: 'right',
      }}>{file.size.trim()}</div>
      <div style={{
        ...fbCol, width: 92, color: C.fg3,
        fontFamily: "'JetBrains Mono', monospace", fontSize: '.74rem',
        textAlign: 'right',
      }}>{file.rows.toLocaleString()}</div>
      <div style={{
        ...fbCol, width: 132, color: C.fg5,
        fontFamily: "'JetBrains Mono', monospace", fontSize: '.72rem',
      }}>{file.modified}</div>
    </div>
  );
}

const FORMAT_STYLES = {
  csv: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok },
  xlsx: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok },
  xls: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok },
  xlsm: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok },
  xlsb: { bg: 'var(--ok-bg)', bd: 'var(--ok-border)', fg: C.ok },
};

function FormatBadge({ format, small }) {
  const s = FORMAT_STYLES[format] || FORMAT_STYLES.csv;
  return (
    <span style={{
      background: s.bg, border: `1px solid ${s.bd}`,
      color: s.fg,
      fontSize: small ? '.62rem' : '.66rem',
      fontWeight: 700, letterSpacing: '.06em',
      padding: small ? '2px 5px' : '3px 6px',
      borderRadius: 4,
      textTransform: 'uppercase',
      display: 'inline-block',
    }}>{format}</span>
  );
}

function FileGlyph({ format }) {
  const s = FORMAT_STYLES[format] || FORMAT_STYLES.csv;
  return (
    <span style={{
      flexShrink: 0,
      width: 26, height: 30, borderRadius: 3,
      background: s.bg, border: `1px solid ${s.bd}`,
      color: s.fg,
      fontSize: '.58rem', fontWeight: 700,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      letterSpacing: '.04em',
    }}>{format.toUpperCase()}</span>
  );
}

const fbHeaderRow = {
  display: 'flex', alignItems: 'center',
  padding: '8px 16px',
  position: 'sticky', top: 0, zIndex: 2,
  background: C.bar,
  borderBottom: `1px solid ${C.border}`,
};
const fbCol = {
  fontSize: '.7rem',
  textTransform: 'uppercase',
  letterSpacing: '.08em',
  fontWeight: 700,
  padding: '0 6px',
};

// ── Output folder picker modal ──────────────────────────────────────────
// Small picker that lets the user pick a destination folder from a
// curated list (recent, mounted shares, S3) or paste / type a custom path.
export function FolderPickerModal({ open, onClose, onPick, currentPath = '' }) {
  const [path, setPath] = React.useState(currentPath || '');
  const [filter, setFilter] = React.useState('');

  React.useEffect(() => {
    if (open) {
      setPath(currentPath || '');
      setFilter('');
    }
  }, [open, currentPath]);

  if (!open) return null;

  const folders = OUTPUT_FOLDERS.filter(f =>
    !filter.trim() || f.path.toLowerCase().includes(filter.toLowerCase()));
  const recent = folders.filter(f => f.recent);
  const other = folders.filter(f => !f.recent);
  const valid = path.trim().length > 0;
  const isCurrent = valid && path.trim() === (currentPath || '').trim();

  const confirm = () => {
    if (!valid || isCurrent) return;
    onPick(path.trim());
  };

  return (
    <div
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onClose();
        if (e.key === 'Enter') confirm();
      }}
      tabIndex={-1}
      style={{
        position: 'fixed', inset: 0, zIndex: 2110, // above the file browser (2100)
        background: 'rgba(15,17,23,.6)',
        backdropFilter: 'blur(3px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: 40,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: 'min(640px, 100%)',
          maxHeight: 'min(620px, 100%)',
          background: C.app,
          border: `1px solid ${C.borderStrong}`,
          borderRadius: 12,
          boxShadow: '0 24px 64px rgba(0,0,0,.6)',
          display: 'flex', flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Title bar */}
        <div style={{
          padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
          display: 'flex', alignItems: 'center', gap: 12,
          background: C.bar,
        }}>
          <span style={{ display: 'inline-flex', color: C.fg3 }}><Folder size={18} /></span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ color: C.fg1, fontWeight: 700, fontSize: '.94rem' }}>
              Choose output folder
            </div>
            <div style={{ color: C.fg5, fontSize: '.74rem', marginTop: 1 }}>
              Pick a recent destination or paste any path.
            </div>
          </div>
          <IconButton title="Close (Esc)" onClick={onClose}><X size={14} /></IconButton>
        </div>

        {/* Path field */}
        <div style={{ padding: '14px 20px', borderBottom: `1px solid ${C.border}` }}>
          <label style={{
            display: 'block',
            color: C.fg5, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
            marginBottom: 6,
          }}>Path</label>
          <input
            value={path}
            onChange={(e) => setPath(e.target.value)}
            placeholder="/Users/dpipe/Exports/…"
            spellCheck={false}
            autoFocus
            style={{
              width: '100%',
              background: C.input,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 8,
              padding: '9px 12px',
              color: C.fg1,
              fontSize: '.86rem',
              fontFamily: "'JetBrains Mono', monospace",
              outline: 'none',
              boxSizing: 'border-box',
            }}
          />
        </div>

        {/* Browse local folder (File System Access API) */}
        {typeof window.showDirectoryPicker === 'function' && (
          <div style={{ padding: '10px 20px', borderBottom: `1px solid ${C.border}` }}>
            <button
              onClick={async () => {
                try {
                  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
                  setOutputDirHandle(handle);
                  onPick(handle.name);
                } catch (_e) { /* user cancelled */ }
              }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10,
                padding: '10px 14px',
                background: C.input, border: `1px solid ${C.accentMuted}`,
                borderRadius: 8, cursor: 'pointer',
                fontFamily: 'inherit', textAlign: 'left',
                transition: 'all .15s',
              }}
            >
              <span style={{
                width: 28, height: 24, borderRadius: 4,
                background: 'var(--info-bg)', border: `1px solid ${C.accentMuted}`,
                color: C.accentBright, fontSize: '.86rem',
                display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
              }}><FolderOpen size={14} /></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ color: C.accentBright, fontSize: '.82rem', fontWeight: 600 }}>
                  Browse local folder…
                </div>
                <div style={{ color: C.fg5, fontSize: '.7rem', marginTop: 1 }}>
                  Pick a folder from your file system to write output directly
                </div>
              </div>
            </button>
          </div>
        )}

        {/* Filter */}
        <div style={{
          padding: '10px 20px 8px',
          display: 'flex', alignItems: 'center', gap: 10,
          borderBottom: `1px solid ${C.border}`,
        }}>
          <span style={{
            color: C.fg5, fontSize: '.66rem',
            textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
            flex: 1,
          }}>Suggestions</span>
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter…"
            style={{
              background: C.input,
              border: `1px solid ${C.borderStrong}`,
              borderRadius: 6,
              padding: '5px 10px',
              color: C.fg2, fontSize: '.76rem',
              fontFamily: 'inherit',
              outline: 'none', width: 180,
            }}
          />
        </div>

        {/* Folder list */}
        <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
          {folders.length === 0 && (
            <div style={{
              padding: '40px 24px', textAlign: 'center', color: C.fg5,
              fontSize: '.78rem',
            }}>No folders match that filter.</div>
          )}
          {recent.length > 0 && (
            <FolderGroup
              label="Recently used"
              folders={recent}
              activePath={path.trim()}
              onPick={(p) => setPath(p)}
            />
          )}
          {other.length > 0 && (
            <FolderGroup
              label="Other locations"
              folders={other}
              activePath={path.trim()}
              onPick={(p) => setPath(p)}
            />
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '12px 20px', borderTop: `1px solid ${C.border}`,
          background: C.bar,
          display: 'flex', alignItems: 'center', gap: 12,
        }}>
          <div style={{
            flex: 1, color: C.fg5, fontSize: '.74rem',
          }}>
            {valid
              ? <span style={{ fontFamily: "'JetBrains Mono', monospace", color: C.fg2 }}>{path.trim()}</span>
              : 'Pick or type a folder path.'}
          </div>
          <Button kind="ghost" size="sm" onClick={onClose}>Cancel</Button>
          <Button kind="primary" size="sm" disabled={!valid || isCurrent} onClick={confirm}>
            {isCurrent ? 'Already selected' : 'Use this folder'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function FolderGroup({ label, folders, activePath, onPick }) {
  return (
    <div>
      <div style={{
        padding: '8px 20px 4px',
        color: C.fg5, fontSize: '.66rem',
        textTransform: 'uppercase', letterSpacing: '.1em', fontWeight: 700,
        background: C.bar,
        borderBottom: `1px solid ${C.borderSubtle}`,
      }}>{label}</div>
      {folders.map(f => {
        const on = activePath === f.path;
        return (
          <button
            key={f.path}
            onClick={() => onPick(f.path)}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', gap: 12,
              padding: '9px 20px',
              background: on ? 'var(--info-bg)' : 'transparent',
              border: 'none',
              borderLeft: `3px solid ${on ? C.accent : 'transparent'}`,
              borderBottom: `1px solid ${C.borderSubtle}`,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'inherit',
            }}
          >
            <span style={{
              width: 26, height: 22, borderRadius: 4,
              background: on ? 'var(--info-bg)' : C.input,
              border: `1px solid ${on ? C.accentMuted : C.borderStrong}`,
              color: on ? C.accentBright : C.fg4,
              fontSize: '.78rem',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}><Folder size={13} /></span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                color: on ? C.fg1 : C.fg2,
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: '.82rem', fontWeight: 600,
                overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}>{f.path}</div>
              <div style={{
                color: C.fg5, fontSize: '.7rem', marginTop: 1,
              }}>{f.hint}</div>
            </div>
          </button>
        );
      })}
    </div>
  );
}
